import { Mutex } from 'async-mutex'
import { t } from 'i18next'
import { atom, useAtom, useSetAtom } from 'jotai'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { getCurrentAuthState } from '~/atoms/AuthenticationState'
import { getMangoApiClient } from '~/atoms/MangoApi'
import { Extension, ParkingLot, useSettingsState } from '~/atoms/SettingsState'
import { useSipInstance } from '~/atoms/SipInstanceState'
import { useUserState } from '~/atoms/UserState'
import {
  ConferenceStateResponse,
  PhoneStatus,
  TrackedExtension,
  TrackedParkingLot,
  useWebphoneState,
} from '~/atoms/WebphoneState'
import { useAnalytics } from '~/hooks/useAnalytics'
import { APP_VERSION_PLACEHOLDER, useAppVersion } from '~/hooks/useAppVersion'
import { useBeforeunload } from '~/hooks/useBeforeunload'
import { useAnnounceCallFailure } from '~/hooks/useCallAnnouncementRelay'
import { useCallPopEnabled } from '~/hooks/useCallPopEnabled'
import { QueueAgentStatus, useCallQueue } from '~/hooks/useCallQueue'
import { useOnLogout } from '~/hooks/useOnLogout'
import { useSharedVariable } from '~/hooks/useSharedVariable'
import { useWebSocketService } from '~/hooks/useWebSocketService'
import {
  ContactDetails,
  ContactNumber,
} from '~/pages/Contacts/viewModels/Contacts.viewmodel'
import { useRecentCalls } from '~/pages/WebPhone/atoms/RecentCalls.atom'
import {
  PARKING_LOT_PREFIX_DIGITS,
  PARKING_LOT_PREFIX_STRING,
  PRESENCE_SUBSCRIPTION_REFRESH_INTERVAL,
} from '~/pages/WebPhone/utils/constants'
import { useGetSipConnectionInfo } from '~/queries/sip'
import {
  NotificationDisposition,
  NotificationDomain,
  NotificationEmitterEventType,
} from '~/services/EventEmitter/types/NotificationEmitterTypes'
import { AnalyticsNamespace } from '~/types/Analytics'
import { WebPhoneAnalyticsEvents } from '~/types/Analytics/WebPhoneAnalyticsEvents'
import { SpeedDialState, TransportConnectionState } from '~/types/WebphoneTypes'
import { WebSocketEvent, WebSocketEventType } from '~/types/WebSocketTypes'
import { Analytics } from '~/utils/analytics'
import { isElectron } from '~/utils/common'
import { isAccessTokenExpired } from '~/utils/functions'
import {
  getContactFromWebSocketMessage,
  getContactFullName,
} from '~/utils/phoneHelpers'
import {
  DeskphoneCallLifecycleTracker,
  shouldEmitDeskphoneLifecycle,
  toAnalyticsData,
} from '~/utils/sipAnalytics'
import {
  OutboundCallProgressEvent,
  PresenceStateChange,
  SESSION_NOT_FOUND_ERROR_MESSAGE,
  SessionStateChange,
  SipClient,
  USER_AGENT_NOT_CONNECTED_ERROR_MESSAGE,
} from '~/utils/sipclient'
import { PresenceState } from '~/utils/xml'

import { FeatureFlag } from '@mangovoice/constants'
import {
  toDialTarget,
  toMaskedPhoneNumber,
  useFeatureFlag,
} from '@mangovoice/core'
import {
  RegistererState,
  RequestPendingError,
  SessionState,
} from '@mangovoice/sip'
import {
  ExtensionPreview,
  isPreConnectedStatus,
  PhoneCall,
  PhoneCallStatus,
  SipConnectionInfo,
} from '@mangovoice/types'

import { WebphoneOperation } from './toast/eventHandlers/webphoneToastEventHandler'
import { useToastAlert } from './toast/useToastAlerts'
import useCloudwatchLogger from './useCloudwatchLogger'
import { useEmitterService } from './useEmitterService'

const MAX_CALLS = 2
const CLIENT_AUDIO_STREAM_KEY = 'client'

let _sipClientInitializing = false

const needsAudioConnectedAtom = atom<boolean>(false)
const needsClientAudioInputAtom = atom<boolean>(false)
// Exported for tests (mirrors registrationStatusAtom).
export const connectionRefreshRequestedAtom = atom<boolean>(false)
export const registrationStatusAtom = atom<RegistererState>(
  RegistererState.Initial,
)
export const registrationErrorAtom = atom<Error | null>(null)

const isPhoneCall = (value: unknown): value is PhoneCall => {
  return (
    value !== null &&
    typeof value === 'object' &&
    'callUuid' in value &&
    typeof (value as PhoneCall).callUuid === 'string'
  )
}

type CallAudio = {
  stream: MediaStream
  audioElement?: HTMLAudioElement
}

const callAudioStreamsAtom = atom<Map<string, CallAudio>>(
  new Map<string, CallAudio>(),
)

// Shared so usePhoneEndpoint can request the audio teardown that usePhoneHub owns
const needsAudioCleanupCheckAtom = atom<boolean>(false)

const useRemoveCallAudioStream = () => {
  const setCallAudioStreams = useSetAtom(callAudioStreamsAtom)
  return useCallback(
    (callUuid: string) => {
      setCallAudioStreams((list) => {
        const newStreams = new Map(list)
        newStreams.delete(callUuid)
        return newStreams
      })
    },
    [setCallAudioStreams],
  )
}

/**
 * Pick the call that should become active after `removedCallUuids` are torn down.
 * Mirrors the SIP Terminated handlers: prefer a connected call that is not on hold,
 * otherwise take the first survivor. Deskphone calls are skipped because switchCall
 * and startCall both refuse to make them active, so promoting one would leave the UI
 * pointing at a call the SIP layer cannot drive.
 */
const findNextActiveCall = (
  callList: Map<string, PhoneCall>,
  removedCallUuids: Set<string>,
): PhoneCall | undefined => {
  const remaining = Array.from(callList.values()).filter(
    (call) =>
      !removedCallUuids.has(call.callUuid) && call.callSource !== 'deskphone',
  )
  return (
    remaining.find(
      (call) => call.status === PhoneCallStatus.CONNECTED && !call.isOnHold,
    ) ?? remaining[0]
  )
}

export { TransportConnectionState } from '~/types/WebphoneTypes'
export const transportConnectionStateAtom = atom<TransportConnectionState>(
  TransportConnectionState.INITIAL,
)
// Set only when the active SIP client reports a lost transport. The
// registration cleanup also writes DISCONNECTED when it replaces the client,
// and reconnecting on that value alone re-registers without end.
export const transportDropReportedAtom = atom(false)

// Module-level Set to track call UUIDs that WebSocket handler decided to reject
// Used to prevent race condition where WebSocket rejects but SIP INVITE arrives later
// SIP handler checks this and rejects calls that were already rejected by WebSocket
// Using module-level variable because both usePhoneHub (SIP) and usePhoneEndpoint (WebSocket) need access
const rejectedCallUuids = new Set<string>()

const sortRegistrationServers = (
  servers: SipConnectionInfo['registration_servers'] = [],
) =>
  [...servers].sort(
    (left, right) =>
      left.sip_host.localeCompare(right.sip_host) ||
      left.sip_port - right.sip_port,
  )

const getSipConnectionInfoKey = (connectionInfo: SipConnectionInfo) =>
  JSON.stringify({
    domain: connectionInfo.domain,
    display_name: connectionInfo.display_name,
    username: connectionInfo.username,
    password: connectionInfo.password,
    registration_servers: sortRegistrationServers(
      connectionInfo.registration_servers,
    ).map((server) => [server.sip_host, server.sip_port]),
  })

const sipConnectionInfoMatches = (
  left: SipConnectionInfo,
  right: SipConnectionInfo,
) => getSipConnectionInfoKey(left) === getSipConnectionInfoKey(right)

// Export for testing - allows tests to reset state between runs
export const _testHelpers = {
  clearRejectedCallUuids: () => rejectedCallUuids.clear(),
}

type RejectionReason =
  'hasCallWaiting' | 'queue_agent_already_on_call' | 'already_rejected'

interface SecondCallRejectionParams {
  callList: Map<string, PhoneCall>
  callUuid: string
  hasCallWaiting: boolean
  isQueueCall: boolean
  isInQueue: boolean
  excludeCallUuid?: string
  source: 'sip' | 'websocket'
  logger?: ReturnType<typeof useCloudwatchLogger>['logger']
  extraLogData?: Record<string, unknown>
  sipClient?: { rejectWithBusy: (callUuid: string) => Promise<void> } | null
  removeCall?: (callUuid: string) => void
}

/**
 * Checks if a second call should be rejected and handles all rejection logic.
 *
 * Rejection rules:
 * 1. Already rejected by another handler
 * 2. hasCallWaiting: reject ALL second calls (universal rule)
 * 3. Queue agent + queue call: always reject (only one queue call at a time)
 *
 * This function handles:
 * - Checking if call was already rejected
 * - Checking if there's an active call
 * - Determining rejection reason
 * - Tracking rejection in rejectedCallUuids
 * - Logging the rejection
 * - Rejecting via SIP
 * - Removing call from list
 *
 * @returns true if call was rejected
 */
const checkSecondCallRejection = ({
  callList,
  callUuid,
  hasCallWaiting,
  isQueueCall,
  isInQueue,
  excludeCallUuid,
  source,
  logger,
  extraLogData = {},
  sipClient,
  removeCall,
}: SecondCallRejectionParams): boolean => {
  if (rejectedCallUuids.has(callUuid)) {
    logger?.debug(`Call already rejected, skipping (${source})`, { callUuid })
    if (sipClient) {
      void sipClient.rejectWithBusy(callUuid)
    }
    return true
  }

  const hasActive = Array.from(callList.values()).some(
    (call) =>
      call.callUuid !== excludeCallUuid &&
      call.status !== PhoneCallStatus.ENDED,
  )

  if (!hasActive) {
    return false
  }

  let reason: RejectionReason | undefined
  if (isQueueCall && isInQueue) {
    reason = 'queue_agent_already_on_call'
  } else if (hasCallWaiting) {
    reason = 'hasCallWaiting'
  }

  if (!reason) {
    return false
  }

  // Track rejection so other handler can skip
  rejectedCallUuids.add(callUuid)
  setTimeout(() => {
    rejectedCallUuids.delete(callUuid)
  }, 30000)

  logger?.info(`Second call rejected (${source})`, {
    callUuid,
    reason,
    isQueueCall,
    isInQueue,
    hasCallWaiting,
    ...extraLogData,
  })

  void sipClient?.rejectWithBusy(callUuid)
  removeCall?.(callUuid)
  return true
}

// Watchdog timing constants — exported for testability and discoverability.
// Shared with useOrphanedDeskphoneCleanup (the deskphone watchdog + sweep).
export const RINGING_TIMEOUT_MS = 120000 // 2 minutes
export const DESKPHONE_POST_DISCONNECT_GRACE_MS = 300000 // 5 minutes after disconnect
// Tunable: max age for a CONNECTED deskphone call with no detected disconnect.
// 8 hours covers very long bridged/support calls observed in production;
// shorter values (e.g. 4h) risk false removals for marathon sessions.
export const DESKPHONE_CONNECTED_TIMEOUT_MS = 28800000 // 8 hours
export const WATCHDOG_CHECK_INTERVAL_MS = 10000 // Check every 10 seconds
// A CONNECTED leg answered just before a socket blip may not be CDR-ingested
// into mango-api yet, so a 404 on a young call means "not ingested", not
// "ended". Only treat a 404 as ended once the call is older than this; younger
// ones defer to the watchdog (same rationale as the RINGING 404 path).
export const DESKPHONE_INGESTION_GRACE_MS = 60000 // 1 minute

/**
 * Whether a SOFTPHONE call is stuck pre-connected past the ringing timeout.
 * Deskphone stuck-detection lives in useOrphanedDeskphoneCleanup — softphone
 * calls are the only ones this hub's watchdog handles (it verifies a live SIP
 * session, which deskphone calls never have).
 *
 * isPreConnectedStatus covers INITIAL, CONNECTING, and RINGING — matching the
 * pre-PR watchdog, which also swept stuck CONNECTING calls.
 */
export const isStuckCall = (call: PhoneCall, now: number): boolean => {
  if (isPreConnectedStatus(call.status)) {
    const callStartMs = call.startTime?.getTime()
    if (!callStartMs) return true
    return now - callStartMs > RINGING_TIMEOUT_MS
  }
  return false
}

// Registry of call UUIDs manually dismissed via the popup Dismiss button.
// Module-scoped, NOT per-hook-instance: usePhoneEndpoint is mounted by many
// components while useWebSocketService's subscription registry is keyed by
// token (last writer wins), so the instance whose RINGING handler is live is
// not necessarily the instance whose dismissCall ran (HybridCallManager's, via
// IPC). A per-instance ref would leave the live handler checking an empty set
// and re-creating the dismissed call.
const DISMISSED_CALL_EXPIRY_MS = 300000 // 5 min expiry to prevent memory leaks
const dismissedCallUuids = new Set<string>()
const dismissedCallExpiryTimeouts = new Map<
  string,
  ReturnType<typeof setTimeout>
>()

export const markCallDismissed = (callUuid: string) => {
  dismissedCallUuids.add(callUuid)
  const existing = dismissedCallExpiryTimeouts.get(callUuid)
  if (existing) clearTimeout(existing)
  dismissedCallExpiryTimeouts.set(
    callUuid,
    setTimeout(() => {
      dismissedCallUuids.delete(callUuid)
      dismissedCallExpiryTimeouts.delete(callUuid)
    }, DISMISSED_CALL_EXPIRY_MS),
  )
}

export const clearCallDismissed = (callUuid: string) => {
  dismissedCallUuids.delete(callUuid)
  const timeoutId = dismissedCallExpiryTimeouts.get(callUuid)
  if (timeoutId) {
    clearTimeout(timeoutId)
    dismissedCallExpiryTimeouts.delete(callUuid)
  }
}

export const isCallDismissed = (callUuid: string): boolean =>
  dismissedCallUuids.has(callUuid)

/**
 * Hub for all phone endpoints in the app.
 * Only one should be live at a time.
 * @returns
 */
export const usePhoneHub = (audioElementId: string) => {
  const appVersion = useAppVersion()
  const { track } = useAnalytics(AnalyticsNamespace.WEB_PHONE)
  const { toastError } = useToastAlert()

  const [initialSetup, setInitialSetup] = useState(true)
  const [transportReconnect, setTransportReconnect] = useState(false)
  // A replaced client takes its sessions with it, like a dropped transport,
  // but must not hold the reconnect latch: a replacement that fails to connect
  // still needs its one automatic reconnect.
  const [staleCallSweepPending, setStaleCallSweepPending] = useState(false)
  const [needsReregister, setNeedsReregister] = useState(false)
  const [currentAudioInput, setCurrentAudioInput] = useState<string>('')
  const [currentAudioOutput, setCurrentAudioOutput] = useState<string>('')

  const audioContext = useRef<AudioContext | undefined>(undefined)
  // The disposable mic-track clone attached to the single-call sender. It shares
  // the live mic source, so it must be stopped on teardown or the OS mic
  // indicator stays lit after the call ends. Not tracked in callAudioStreams
  // (that map holds the shared mic + remote streams, not per-send clones).
  const singleCallMicClone = useRef<MediaStreamTrack | undefined>(undefined)
  // Mirrored to a ref so connectAudio reads the latest value without changing
  // its identity when flags load.
  const singleCallAudioBypassRef = useRef(false)
  singleCallAudioBypassRef.current = useFeatureFlag(
    FeatureFlag.WebphoneSingleCallAudioBypass,
  ).isEnabled
  const [callAudioStreams, setCallAudioStreams] = useAtom(callAudioStreamsAtom)

  // Refs to hold latest values for watchdog (avoids stale closures)
  const watchdogRefs = useRef<{
    callList: Map<string, PhoneCall>
    removeCall: (callUuid: string) => void
    pendingInvitationUuid: string | null
    setPendingInvitationUuid: (uuid: string | null) => void
    findNextRingingCallHub:
      ((excludeCallUuid?: string) => PhoneCall | undefined) | null
    sipSessionExists: ((callUuid: string) => boolean) | null
    logger: ReturnType<typeof useCloudwatchLogger>['logger']
    track: (
      name: WebPhoneAnalyticsEvents,
      data?: Record<string, unknown>,
    ) => void
  }>({} as any)

  const [needsClientAudioInput, setNeedsClientAudioInput] = useAtom(
    needsClientAudioInputAtom,
  )
  const [fetchingAudioInput, setFetchingAudioInput] = useState(false)
  const [needsAudioCleanupCheck, setNeedsAudioCleanupCheck] = useAtom(
    needsAudioCleanupCheckAtom,
  )

  const [needsAudioConnected, setNeedsAudioConnected] = useAtom(
    needsAudioConnectedAtom,
  )
  const [connectionRefreshRequested, setConnectionRefreshRequested] = useAtom(
    connectionRefreshRequestedAtom,
  )
  const [registrationState, setRegistrationState] = useAtom(
    registrationStatusAtom,
  )
  const [, setRegistrationError] = useAtom(registrationErrorAtom)
  const [transportConnectionState, setTransportConnectionState] = useAtom(
    transportConnectionStateAtom,
  )
  const [transportDropReported, setTransportDropReported] = useAtom(
    transportDropReportedAtom,
  )

  const { logger } = useCloudwatchLogger('Webphone')
  const { logger: sipLogger } = useCloudwatchLogger('SipClient')

  const {
    settings: {
      webPhone: { allowClickToCallIncoming, hasCallWaiting },
    },
  } = useSettingsState()

  const { currentAgentStatus } = useCallQueue()
  const isInQueue = currentAgentStatus === QueueAgentStatus.InQueue

  const { currentUserExtensionId: userExtensionId, pbxId } = useUserState()
  const { data: sipConn, refetch: requerySipConnectionInfo } =
    useGetSipConnectionInfo({ extensionId: userExtensionId ?? 0 })
  const sipConnectionInfoKey = useMemo(
    () => (sipConn ? getSipConnectionInfoKey(sipConn) : null),
    [sipConn],
  )
  const previousSipConnectionInfoRef = useRef<SipConnectionInfo | null>(null)
  const sipConnectionInfoWasUnavailableRef = useRef(false)

  const {
    triggerReregister,
    sipInstance: [getSipInstance, setSipInstance],
  } = useSipInstance()
  const activeSipClientRef = useRef<SipClient | null>(null)

  useLayoutEffect(() => {
    if (!initialSetup) return
    setRegistrationError(null)
    setRegistrationState(RegistererState.Initial)
    setTransportConnectionState(TransportConnectionState.INITIAL)
    setTransportReconnect(false)
  }, [initialSetup])

  const {
    phoneStatus,
    phoneCalls: {
      active: { activeCall, activeCallUuid, setActiveCallUuid },
      list: { callList: rawCallList, upsertCall, removeCall },
      conference: { conferencedCalls, isMuted: conferenceIsMuted },
      invite: {
        pendingInvitation,
        pendingInvitationUuid,
        setPendingInvitationUuid,
      },
    },
    trackedParkingLots,
    updateTrackedParkingLot,
    trackedExtensions,
    updateTrackedExtension,
    audio: {
      output: {
        preference: [selectedOutputDeviceId],
        setAudioOutputStream,
      },
      input: {
        preference: [selectedInputDeviceId],
        getAudioInputStream,
      },
    },
  } = useWebphoneState({
    pbxId: pbxId ?? 0,
    registrationState,
    transportConnectionState,
  })

  // Keep a ref to the latest rawCallList to avoid stale closure issues in callbacks
  // This prevents race conditions where event handlers see stale state
  // Use useLayoutEffect to ensure synchronous update before any effects run
  const callListRef = useRef(rawCallList)
  useLayoutEffect(() => {
    callListRef.current = rawCallList
  }, [rawCallList])

  // Track current activeCallUuid to detect call switches during async operations
  const activeCallUuidRef = useRef(activeCallUuid)
  useLayoutEffect(() => {
    activeCallUuidRef.current = activeCallUuid
  }, [activeCallUuid])

  // Track current activeCall and conferencedCalls via refs for use in effects with stable dependencies
  const activeCallRef = useRef(activeCall)
  const conferencedCallsRef = useRef(conferencedCalls)
  useLayoutEffect(() => {
    activeCallRef.current = activeCall
    conferencedCallsRef.current = conferencedCalls
  }, [activeCall, conferencedCalls])

  // Track call UUIDs that received a SIP INVITE (either processed or rejected)
  // These should not be created as desk phone calls when WebSocket events arrive
  // This prevents race conditions when browser throttling delays SIP invite processing
  const sipInviteReceivedRef = useRef<Set<string>>(new Set())

  // Single-flight guard: coalesces overlapping reconnect requests so the
  // client isn't torn down twice in a row during the connect window. Uses the
  // codebase's Mutex.isLocked() pattern (see WebSocketService) — the request
  // is intentionally dropped (not queued via runExclusive/waitForUnlock),
  // since a queued second reset is exactly the re-register churn we remove.
  const hardResetMutexRef = useRef(new Mutex())

  const hardResetSipClient = async () => {
    if (hardResetMutexRef.current.isLocked()) {
      logger?.debug('hardResetSipClient already in progress, skipping')
      return
    }
    const release = await hardResetMutexRef.current.acquire()
    try {
      // Await the refetch to ensure we have fresh credentials before reinitializing
      // Use { cancelRefetch: false } to ensure we get fresh data even if a refetch is in progress
      const result = await requerySipConnectionInfo()
      // Only trigger re-registration if the refetch was successful
      if (result.data) {
        triggerReregister(setNeedsReregister)
      } else {
        // Cold-start path only: useGetSipConnectionInfo now rejects a payload
        // with no registration servers, so a first-ever fetch lands here
        // instead of throwing from the SipClient constructor. A PBX that
        // breaks after a good fetch keeps that stale `result.data` on error
        // and re-registers via the branch above without reaching this log.
        logger?.error(
          'Failed to refetch SIP connection info during reconnect',
          null,
          { extensionId: userExtensionId, error: result.error },
        )
      }
    } finally {
      release()
    }
  }

  // Handle connection refresh requests
  useEffect(() => {
    if (connectionRefreshRequested) {
      logger?.info('Connection refresh requested, refetching SIP credentials')
      // Overlapping requests are coalesced (dropped), not queued. Recovery
      // does not depend on the dropped request being retried directly:
      //  - The connection-refresh caller (resetConnection / CallErrorView's
      //    retry loop) keeps re-issuing requests until phoneStatus is READY.
      //  - The transport-reconnect caller does NOT re-fire on its own (it
      //    latches transportReconnect=true and only clears at READY), so a
      //    dropped transport reset relies on the CallErrorView retry loop to
      //    drive recovery while isConnected is false.
      // An imperative one-shot caller would need its own retry.
      void hardResetSipClient().catch((error) => {
        logger?.error('Failed to reset SIP client during reconnect', error)
      })
      setConnectionRefreshRequested(false)
    }
  }, [connectionRefreshRequested])

  // If the SIP connection info changes, trigger a re-registration
  useEffect(() => {
    if (!sipConn) {
      if (previousSipConnectionInfoRef.current) {
        sipConnectionInfoWasUnavailableRef.current = true
      }
      return
    }
    const previousSipConn = previousSipConnectionInfoRef.current
    if (!previousSipConn) {
      previousSipConnectionInfoRef.current = sipConn
      return
    }
    const wasUnavailable = sipConnectionInfoWasUnavailableRef.current
    sipConnectionInfoWasUnavailableRef.current = false
    if (!wasUnavailable && sipConnectionInfoMatches(previousSipConn, sipConn)) {
      return
    }
    previousSipConnectionInfoRef.current = sipConn
    triggerReregister(setNeedsReregister)
    // Only depends on sipConn by design: triggerReregister's identity changes
    // with the SIP client, so including it would re-fire and re-trigger the
    // redundant re-registration this fix removes.
  }, [sipConn])

  // Handle transport reconnection
  useEffect(() => {
    if (initialSetup || needsReregister) return
    if (
      !transportReconnect &&
      transportDropReported &&
      phoneStatus === PhoneStatus.TRANSPORT_DISCONNECTED
    ) {
      setTransportDropReported(false)
      hardResetSipClient()
      setTransportReconnect(true)
    }
    // Gate the reconnect completion on the transport itself, not on phoneStatus.
    // getPhoneStatus() derives CALL_ACTIVE from the active call's status, so a stale
    // CONNECTED call pins phoneStatus at CALL_ACTIVE and it never reaches READY -
    // which is exactly the state the cleanup below exists to resolve. needsReregister
    // is already false here, so getSipInstance() is the post-reconnect client.
    if (
      (transportReconnect || staleCallSweepPending) &&
      transportConnectionState === TransportConnectionState.CONNECTED
    ) {
      setTransportReconnect(false)
      setStaleCallSweepPending(false)
      // After a transport reconnect the SIP client is re-initialized and all SIP
      // sessions are gone. Any softphone call still marked CONNECTED in the callList
      // no longer has a backing SIP session, and no Terminated event will ever arrive
      // to tear it down. Remove it here so it stops counting against MAX_CALLS,
      // blocking beforeunload and holding audio resources open.
      // Pre-connected calls are deliberately left to the orphaned-call watchdog: a
      // softphone call created from a WebSocket event can briefly have no session
      // while the SIP INVITE handler catches up.
      // Use callListRef.current (not rawCallList) to read the latest call state,
      // consistent with how the rest of this file avoids stale closures in effects.
      const sipClient = getSipInstance()
      if (!sipClient) return
      const staleCalls = Array.from(callListRef.current.values()).filter(
        (call) =>
          call.callSource === 'softphone' &&
          call.status === PhoneCallStatus.CONNECTED &&
          !sipClient.hasSession(call.callUuid),
      )
      if (staleCalls.length === 0) return

      staleCalls.forEach((call) => {
        logger?.warn(
          'Cleaning up stale softphone call after SIP reconnect - no SIP session found',
          { callUuid: call.callUuid, callNumber: call.number },
        )
        upsertCall({ ...call, status: PhoneCallStatus.ENDED })
        removeCall(call.callUuid)
        removeCallAudioStream(call.callUuid)
      })
      setNeedsAudioCleanupCheck(true)

      // Promote a survivor only once the whole sweep is known - callListRef is not
      // updated until the next render, so picking per-call could promote a call that
      // this same pass is about to remove.
      const staleCallUuids = new Set(staleCalls.map((call) => call.callUuid))
      if (
        activeCallUuidRef.current &&
        staleCallUuids.has(activeCallUuidRef.current)
      ) {
        const nextActiveCall = findNextActiveCall(
          callListRef.current,
          staleCallUuids,
        )
        setActiveCallUuid(nextActiveCall?.callUuid ?? null)
        if (
          nextActiveCall?.status === PhoneCallStatus.CONNECTED &&
          !nextActiveCall.isOnHold
        ) {
          setNeedsAudioConnected(true)
        }
      }
    }
  }, [
    transportReconnect,
    staleCallSweepPending,
    transportDropReported,
    phoneStatus,
    transportConnectionState,
    initialSetup,
    needsReregister,
  ])

  // The reconnect sweep and the deskphone watchdog now live in
  // useOrphanedDeskphoneCleanup (mounted by HybridCallManager) so deskphone-only
  // users get the safety net too. The softphone watchdog below stays here,
  // where a live SIP session exists to verify against.

  const getCallerInfo = useCallback(
    (callUuid: string): { name: string | null; number: string } => {
      const sipClient = getSipInstance()
      if (!sipClient) {
        logger?.debug('No SIP client available for remote caller info', {
          callUuid,
        })
        return { name: null, number: 'Unknown Number' }
      }
      try {
        const { displayName: name, user: number } =
          sipClient.getRemoteCallerId(callUuid)
        return { name, number: number ?? 'Unknown Number' }
      } catch {
        logger?.debug('Failed to find remote caller info', { callUuid })
        return { name: null, number: 'Unknown Number' }
      }
    },
    [getSipInstance],
  )

  // TODO: Wrap the audio stream atom management in a separate hook
  const upsertCallAudioStream = useCallback(
    (
      callUuid: string,
      stream: MediaStream,
      audioElement?: HTMLAudioElement,
    ) => {
      setCallAudioStreams((list) => {
        const newStreams = new Map(list)
        newStreams.set(callUuid, { stream, audioElement })
        return newStreams
      })
    },
    [setCallAudioStreams],
  )

  const removeCallAudioStream = useRemoveCallAudioStream()

  const clearCallAudioStreams = () => {
    callAudioStreams.forEach((callAudio) => {
      callAudio.stream.getTracks().forEach((t) => t.stop())
      if (callAudio.audioElement) {
        callAudio.audioElement.srcObject = null
        callAudio.audioElement.remove()
      }
    })
    // The single-call mic clone isn't in callAudioStreams; stop it here so the
    // OS mic indicator clears when the last call ends.
    singleCallMicClone.current?.stop()
    singleCallMicClone.current = undefined
    setCallAudioStreams(new Map<string, CallAudio>())
  }

  const getCallAudioStream = useCallback(
    (callUuid: string) => {
      return callAudioStreams.get(callUuid)
    },
    [callAudioStreams],
  )

  // Handle client audio input needs
  useEffect(() => {
    if (needsClientAudioInput && !getCallAudioStream(CLIENT_AUDIO_STREAM_KEY)) {
      const sipClient = getSipInstance()
      if (sipClient && !fetchingAudioInput) {
        setFetchingAudioInput(true)
        getAudioInputStream()
          .then((stream) => {
            upsertCallAudioStream(CLIENT_AUDIO_STREAM_KEY, stream)
            setNeedsClientAudioInput(false)
          })
          .catch((error) => {
            // Reset needsClientAudioInput to prevent infinite loop
            setNeedsClientAudioInput(false)

            // NotReadableError = the OS/another app is holding the mic. This is
            // an environmental condition, not an app fault — surface clear
            // guidance to the user and log at warn (not error) so it doesn't
            // escalate as noise in Sentry on every retry.
            if (
              error instanceof DOMException &&
              error.name === 'NotReadableError'
            ) {
              logger?.warn('Microphone is in use by another application', {
                error: error.message,
              })
              toastError(
                t('webPhone.callError.microphoneInUse'),
                t('webPhone.callError.microphoneInUseText'),
              )
              return
            }

            // Handle OverconstrainedError and other getUserMedia errors
            logger?.error('Failed to get audio input stream', error)
          })
          .finally(() => {
            setFetchingAudioInput(false)
          })
      }
    }
  }, [
    needsClientAudioInput,
    getCallAudioStream,
    getSipInstance,
    fetchingAudioInput,
    getAudioInputStream,
    upsertCallAudioStream,
  ])

  // Handle audio cleanup
  useEffect(() => {
    if (needsAudioCleanupCheck) {
      if (rawCallList.size === 0) {
        try {
          audioContext.current?.close().then(() => {
            audioContext.current = undefined
          })
        } catch (_e) {
          logger?.info('Error closing audio context, likely already closed')
        }
        clearCallAudioStreams()
      }
      setNeedsAudioCleanupCheck(false)
    }
  }, [needsAudioCleanupCheck, rawCallList.size])

  // Helper to check if SIP session exists for a call
  const sipSessionExists = useCallback(
    (callUuid: string): boolean => {
      const sipClient = getSipInstance()
      if (!sipClient) return false

      return sipClient.hasSession(callUuid)
    },
    [getSipInstance],
  )

  // Helper function to find next ringing call for FIFO queue (usePhoneHub)
  const findNextRingingCallHub = useCallback(
    (excludeCallUuid?: string) => {
      return Array.from(rawCallList.values())
        .filter(
          (call) =>
            call.callUuid !== excludeCallUuid &&
            isPreConnectedStatus(call.status),
        )
        .sort(
          (a, b) =>
            (a.startTime?.getTime() ?? 0) - (b.startTime?.getTime() ?? 0),
        )[0]
    },
    [rawCallList],
  )

  // Keep watchdog refs up-to-date
  useEffect(() => {
    watchdogRefs.current = {
      callList: rawCallList,
      removeCall,
      pendingInvitationUuid,
      setPendingInvitationUuid,
      findNextRingingCallHub,
      sipSessionExists,
      logger,
      track,
    }
  }, [
    rawCallList,
    removeCall,
    pendingInvitationUuid,
    setPendingInvitationUuid,
    findNextRingingCallHub,
    sipSessionExists,
    logger,
    track,
  ])

  // Watchdog timer to detect and clean up orphaned SOFTPHONE calls. Deskphone
  // cleanup lives in useOrphanedDeskphoneCleanup (see above); this path only
  // handles softphone calls, which require a live SIP session to verify — so it
  // stays in the hub, where the SIP client exists. Behavior is unchanged from
  // pre-PR.
  useEffect(() => {
    const checkForStuckCalls = () => {
      const now = Date.now()
      const refs = watchdogRefs.current
      const stuckCalls = Array.from(refs.callList.values()).filter(
        (call) => call.callSource !== 'deskphone' && isStuckCall(call, now),
      )

      if (stuckCalls.length > 0) {
        refs.logger?.warn('Detected stuck calls, validating', {
          stuckCallsCount: stuckCalls.length,
        })

        stuckCalls.forEach((call) => {
          try {
            // Softphone calls: verify the SIP session is actually gone
            const sessionExists = refs.sipSessionExists?.(call.callUuid)

            if (!sessionExists) {
              refs.logger?.warn('Orphaned call detected and removed', {
                callUuid: call.callUuid,
                status: call.status,
                number: call.number,
                ageSeconds: call.startTime
                  ? Math.floor((now - call.startTime.getTime()) / 1000)
                  : 'unknown',
              })
              refs.track?.(WebPhoneAnalyticsEvents.OrphanedCallCleanup, {
                layer: 'watchdog',
                callSource: 'softphone',
                callStatus: call.status,
              })

              refs.removeCall(call.callUuid)

              if (call.callUuid === refs.pendingInvitationUuid) {
                const nextRingingCall = refs.findNextRingingCallHub?.(
                  call.callUuid,
                )
                refs.setPendingInvitationUuid(nextRingingCall?.callUuid ?? null)
              }
            } else {
              refs.logger?.debug('Call is stuck but SIP session still exists', {
                callUuid: call.callUuid,
                status: call.status,
              })
            }
          } catch (error) {
            refs.logger?.error(
              'Error during orphaned call detection',
              error as Error,
              {
                callUuid: call.callUuid,
                error: error instanceof Error ? error.message : String(error),
              },
            )
          }
        })
      }
    }

    const intervalId = setInterval(
      checkForStuckCalls,
      WATCHDOG_CHECK_INTERVAL_MS,
    )
    checkForStuckCalls() // Run immediately on mount

    return () => {
      clearInterval(intervalId)
    }
  }, []) // Empty deps - watchdog runs once, accesses latest values via watchdogRefs

  const updateCallRemoteStream = (callUuid: string) => {
    const sipClient = getSipInstance()
    if (!sipClient) return
    const stream = sipClient.getRemoteStream(callUuid)
    const audio = new Audio()
    audio.srcObject = stream
    audio.muted = true
    upsertCallAudioStream(callUuid, stream, audio)
  }

  const connectAudio = useCallback(
    async (sipClient: SipClient, callUuids: string[], isMuted: boolean) => {
      if (callUuids.length === 0) return
      // Tear down any prior AudioContext (e.g. when dropping back from a
      // conference to a single call). The direct single-call path runs without
      // one, so leaving a stale context around would leak it.
      if (audioContext.current) {
        try {
          await audioContext.current.close()
          logger?.info('connectAudio: closed previous AudioContext')
        } catch (_e) {
          logger?.debug('Error closing audio context, likely already closed')
        }
        audioContext.current = undefined
      }

      const clientInputStream = getCallAudioStream(CLIENT_AUDIO_STREAM_KEY)
      if (!clientInputStream) {
        logger?.info('Client audio stream not found')
        throw new Error('Client audio stream not found')
      }
      clientInputStream.stream.getTracks()[0].enabled = !isMuted

      if (callUuids.length === 1 && singleCallAudioBypassRef.current) {
        const callAudio = getCallAudioStream(callUuids[0])
        if (!callAudio) {
          logger?.info('Call audio stream not found')
          throw new Error('Call audio stream not found')
        }

        // Single-call direct path — NO AudioContext. Routing a lone call
        // through a Web Audio graph adds a resampling stage locked to the
        // output device's sample rate at context-creation time. When macOS
        // CoreAudio renegotiates (output-device switch, sleep/wake, AirPods
        // connect/disconnect) that stage desyncs and renders choppy/underrun
        // audio — see useNetworkConnectivity.ts, where alert sounds were moved
        // off Web Audio for the same reason. Playing the remote stream straight
        // on the <audio> element lets Chromium track device sample-rate changes
        // automatically; the mic goes straight to the peer. The graph is only
        // needed to mix a real N-way conference (handled below).
        logger?.info(
          'connectAudio: single-call direct path (no AudioContext)',
          {
            callUuid: callUuids[0],
          },
        )
        setAudioOutputStream(callAudio.stream)
        // Hand the peer a CLONE of the mic track, never the live track itself.
        // setRemoteStream stops the current sender track before replacing it;
        // connectAudio re-runs on reconnect (an input-device change sets
        // needsAudioConnected), and on that second pass the sender's track is
        // the one we sent last time. Sending the live mic track directly would
        // let setRemoteStream stop the shared mic source, muting us to the
        // remote party for the rest of the call. A clone is disposable —
        // stopping the previous pass's clone leaves the mic source intact. The
        // conference path avoids this by sending a derived destination track;
        // the direct path gets the same safety via clone().
        const micTrack = clientInputStream.stream.getTracks()[0].clone()
        micTrack.enabled = !isMuted
        // Stop the clone from the previous pass now that we're replacing it;
        // setRemoteStream also stops the sender's current track, but a silent
        // no-op (session gone) leaves it attached-but-orphaned, so drop our
        // reference here regardless.
        singleCallMicClone.current?.stop()
        singleCallMicClone.current = undefined
        try {
          const attached = await sipClient.setRemoteStream(
            callUuids[0],
            new MediaStream([micTrack]),
          )
          // Only retain the clone if it actually reached the sender; otherwise
          // it's orphaned and must be stopped so it doesn't hold the mic open.
          if (attached) {
            singleCallMicClone.current = micTrack
          } else {
            micTrack.stop()
          }
        } catch (error) {
          micTrack.stop()
          logger?.warn('Failed to set remote stream for single call', {
            callUuid: callUuids[0],
            error,
          })
        }
      } else {
        // Switching to an AudioContext path: the single-call clone (if any) is
        // about to be replaced on the sender below, so stop it here to avoid a
        // stale ref.
        singleCallMicClone.current?.stop()
        singleCallMicClone.current = undefined
        audioContext.current = new AudioContext()
        logger?.info('connectAudio: created AudioContext', {
          sampleRate: audioContext.current.sampleRate,
          callCount: callUuids.length,
        })
        // Surface CoreAudio interruptions (macOS device transitions) so a
        // future choppy-conference report can be tied to a context state change.
        audioContext.current.onstatechange = () => {
          logger?.info('connectAudio: AudioContext state change', {
            state: audioContext.current?.state,
          })
        }
        const clientSource = audioContext.current.createMediaStreamSource(
          clientInputStream.stream,
        )

        if (callUuids.length === 1) {
          const callAudio = getCallAudioStream(callUuids[0])
          if (!callAudio) {
            logger?.info('Call audio stream not found')
            throw new Error('Call audio stream not found')
          }
          const callSource = audioContext.current.createMediaStreamSource(
            callAudio.stream,
          )

          const callGain = audioContext.current.createGain()
          callGain.gain.value = 1
          callSource.connect(callGain)
          const clientDestination =
            audioContext.current.createMediaStreamDestination()
          callGain.connect(clientDestination)

          const clientGain = audioContext.current.createGain()
          clientGain.gain.value = 1
          clientSource.connect(clientGain)
          const callDestination =
            audioContext.current.createMediaStreamDestination()
          clientGain.connect(callDestination)

          setAudioOutputStream(clientDestination.stream)
          try {
            await sipClient.setRemoteStream(
              callUuids[0],
              callDestination.stream,
            )
          } catch (error) {
            logger?.warn('Failed to set remote stream for single call', {
              callUuid: callUuids[0],
              error,
            })
          }
          return
        }

        const callKeys = [CLIENT_AUDIO_STREAM_KEY, ...callUuids]
        const callAudioSources = new Map<string, MediaStreamAudioSourceNode>()
        callAudioSources.set(CLIENT_AUDIO_STREAM_KEY, clientSource)
        callUuids.forEach((callKey) => {
          if (!audioContext.current) {
            logger?.info('Audio context not found')
            throw new Error('Audio context not found')
          }
          const callAudio = getCallAudioStream(callKey)
          if (!callAudio) {
            logger?.info('Call audio stream not found')
            throw new Error('Call audio stream not found')
          }
          const callSource = audioContext.current.createMediaStreamSource(
            callAudio.stream.clone(),
          )
          callAudioSources.set(callKey, callSource)
        })

        const callAudioDestinations = new Map<
          string,
          MediaStreamAudioDestinationNode
        >()
        callKeys.forEach((callKey) => {
          if (!audioContext.current) {
            logger?.info('Audio context not found')
            throw new Error('Audio context not found')
          }
          const callDestination =
            audioContext.current.createMediaStreamDestination()
          const gainNode = audioContext.current.createGain()
          callAudioSources.forEach((source, key) => {
            if (key !== callKey) {
              source.connect(gainNode)
            }
          })
          gainNode.gain.value = 1
          gainNode.connect(callDestination)
          callAudioDestinations.set(callKey, callDestination)
        })

        const clientStream = callAudioDestinations.get(
          CLIENT_AUDIO_STREAM_KEY,
        )?.stream
        if (!clientStream) {
          logger?.info('Client audio stream not found')
          throw new Error('Client audio stream not found')
        }
        setAudioOutputStream(clientStream)
        for (const callUuid of callUuids) {
          const callStream = callAudioDestinations.get(callUuid)?.stream
          if (!callStream) {
            logger?.info('Call audio stream not found')
            throw new Error('Call audio stream not found')
          }
          try {
            await sipClient.setRemoteStream(callUuid, callStream)
          } catch (error) {
            logger?.warn('Failed to set remote stream for conference call', {
              callUuid,
              error,
            })
          }
        }
      }
    },
    [getCallAudioStream, setAudioOutputStream, audioContext],
  )

  // #region SIP event handlers
  const handleSipClientRegistrationChange = useCallback(
    (state: RegistererState) => {
      setRegistrationState(state)
      logger?.info(`onRegistrationStateChanged: ${state}`)
    },
    [setRegistrationState],
  )

  const handleOutboundSessionStateChange = useCallback(
    (stateChange: SessionStateChange) => {
      logger?.info('Outbound session state change', {
        callUuid: stateChange.callUuid,
        state: stateChange.state,
      })
      const call = callListRef.current.get(stateChange.callUuid)
      if (!call) {
        logger?.warn('Call not found in callList', {
          callUuid: stateChange.callUuid,
          state: stateChange.state,
        })
        return
      }
      const previousStatus = call.status
      switch (stateChange.state) {
        case SessionState.Establishing:
          logger?.info('Outbound call state change', {
            callUuid: stateChange.callUuid,
            previousStatus,
            newStatus: PhoneCallStatus.CONNECTING,
            number: call.number,
          })
          upsertCall({
            ...call,
            status: PhoneCallStatus.CONNECTING,
            startTime: call.startTime,
          })
          break
        case SessionState.Established: {
          logger?.info('Outbound call state change', {
            callUuid: stateChange.callUuid,
            previousStatus,
            newStatus: PhoneCallStatus.CONNECTED,
            number: call.number,
          })
          // Preserve existing startTime to maintain total call duration (used by popup timer)
          // Only set startTime if it's null or undefined (for outbound calls that started with null)
          upsertCall({
            ...call,
            status: PhoneCallStatus.CONNECTED,
            startTime: call.startTime ?? new Date(),
          })
          updateCallRemoteStream(stateChange.callUuid)
          if (stateChange.callUuid === activeCallUuid) {
            setNeedsAudioConnected(true)
          }
          break
        }
        case SessionState.Terminating:
          logger?.info('Outbound call state change', {
            callUuid: stateChange.callUuid,
            previousStatus,
            newStatus: PhoneCallStatus.ENDING_CALL,
            number: call.number,
          })
          upsertCall({
            ...call,
            status: PhoneCallStatus.ENDING_CALL,
          })
          break
        case SessionState.Terminated:
          logger?.info('Outbound call terminated', {
            callUuid: stateChange.callUuid,
            previousStatus,
            number: call.number,
          })
          upsertCall({ ...call, status: PhoneCallStatus.ENDED })
          removeCall(stateChange.callUuid)
          removeCallAudioStream(stateChange.callUuid)

          if (stateChange.callUuid === activeCallUuid) {
            // Check if we have any other calls that should become active
            const remainingCalls = new Map(callListRef.current)
            remainingCalls.delete(stateChange.callUuid)

            if (remainingCalls.size > 0) {
              // Prefer a call that is NOT on hold as the new active call
              const activeCallNotOnHold = Array.from(
                remainingCalls.values(),
              ).find(
                (call) =>
                  call.status === PhoneCallStatus.CONNECTED && !call.isOnHold,
              )

              const newActiveCallUuid = activeCallNotOnHold
                ? activeCallNotOnHold.callUuid
                : remainingCalls.keys().next().value

              if (newActiveCallUuid) {
                const newActiveCall = remainingCalls.get(newActiveCallUuid)
                logger?.info(
                  `Found remaining calls after active call ended, selecting ${newActiveCallUuid} as active (isOnHold: ${newActiveCall?.isOnHold})`,
                )
                setActiveCallUuid(newActiveCallUuid)
                // Only connect audio if the new active call is NOT on hold
                // If it's on hold, we don't want to resume it automatically
                if (newActiveCall && !newActiveCall.isOnHold) {
                  setNeedsAudioConnected(true)
                }
              } else {
                setActiveCallUuid(null)
              }
            } else {
              setActiveCallUuid(null)
            }
          }

          if (stateChange.callUuid === pendingInvitationUuid) {
            // Find next ringing call for FIFO queue
            const nextRingingCall = findNextRingingCallHub(stateChange.callUuid)
            setPendingInvitationUuid(nextRingingCall?.callUuid ?? null)
          }
          setNeedsAudioCleanupCheck(true)
          break
        default:
          break
      }
    },
    [
      activeCallUuid,
      callListRef,
      getCallerInfo,
      findNextRingingCallHub,
      setPendingInvitationUuid,
      pendingInvitationUuid,
    ],
  )

  const handleOutboundCallProgress = useCallback(
    (event: OutboundCallProgressEvent) => {
      const call = callListRef.current.get(event.callUuid)
      if (!call) return

      if (call.status !== PhoneCallStatus.CONNECTING) return

      logger?.info('Outbound call progress', {
        callUuid: event.callUuid,
        statusCode: event.statusCode,
        previousStatus: call.status,
        newStatus: PhoneCallStatus.RINGING,
      })

      upsertCall({
        ...call,
        status: PhoneCallStatus.RINGING,
      })
    },
    [callListRef],
  )

  const handleInboundSessionStateChange = useCallback(
    (stateChange: SessionStateChange) => {
      logger?.info('Inbound session state change', {
        callUuid: stateChange.callUuid,
        state: stateChange.state,
      })
      const { name, number } = getCallerInfo(stateChange.callUuid)
      const call = callListRef.current.get(stateChange.callUuid)
      if (!call) {
        logger?.warn('Call not found in callList', {
          callUuid: stateChange.callUuid,
          state: stateChange.state,
        })
        return
      }
      const previousStatus = call.status
      switch (stateChange.state) {
        case SessionState.Establishing: {
          logger?.info('Inbound call state change', {
            callUuid: stateChange.callUuid,
            previousStatus,
            newStatus: PhoneCallStatus.RINGING,
            number,
          })
          // Preserve contactInfo from WebSocket if available (it's more authoritative)
          // Only update number/name from SIP if we don't have contactInfo
          const hasContactInfo = !!call.contactInfo
          const finalNumber =
            hasContactInfo && call.contactInfo?.phone_number
              ? call.contactInfo.phone_number
              : number
          const finalName =
            hasContactInfo && call.contactInfo
              ? getContactFullName(call.contactInfo) || call.name || name
              : name || call.name

          upsertCall({
            ...call,
            number: finalNumber,
            name: finalName ?? null,
            status: PhoneCallStatus.RINGING,
            startTime: call.startTime,
            // Explicitly preserve contactInfo to ensure it's not lost
            contactInfo: call.contactInfo,
          })
          break
        }
        case SessionState.Established: {
          logger?.info('Inbound call state change', {
            callUuid: stateChange.callUuid,
            previousStatus,
            newStatus: PhoneCallStatus.CONNECTED,
            number,
          })
          // Preserve existing startTime to maintain total call duration (used by popup timer)
          // Only set startTime if it's null or undefined (for outbound calls that started with null)
          upsertCall({
            ...call,
            status: PhoneCallStatus.CONNECTED,
            startTime: call.startTime ?? new Date(),
          })
          updateCallRemoteStream(stateChange.callUuid)
          if (stateChange.callUuid === activeCallUuid) {
            setNeedsAudioConnected(true)
          }
          // Advance FIFO queue if answering the pending invitation
          if (stateChange.callUuid === pendingInvitationUuid) {
            const nextRingingCall = findNextRingingCallHub(stateChange.callUuid)

            setPendingInvitationUuid(nextRingingCall?.callUuid ?? null)
          }
          break
        }
        case SessionState.Terminating:
          logger?.info('Inbound call state change', {
            callUuid: stateChange.callUuid,
            previousStatus,
            newStatus: PhoneCallStatus.ENDING_CALL,
            number,
          })
          upsertCall({
            ...call,
            status: PhoneCallStatus.ENDING_CALL,
          })
          break
        case SessionState.Terminated:
          logger?.info('Inbound call terminated', {
            callUuid: stateChange.callUuid,
            previousStatus,
            number,
          })
          upsertCall({ ...call, status: PhoneCallStatus.ENDED })

          // Advance FIFO queue before removing call from list
          if (stateChange.callUuid === pendingInvitationUuid) {
            const nextRingingCall = findNextRingingCallHub(stateChange.callUuid)
            setPendingInvitationUuid(nextRingingCall?.callUuid ?? null)
          }
          removeCall(stateChange.callUuid)
          removeCallAudioStream(stateChange.callUuid)
          if (stateChange.callUuid === activeCallUuid) {
            setActiveCallUuid(null)
          }
          setNeedsAudioCleanupCheck(true)
          break
        default:
          break
      }
    },
    [
      activeCallUuid,
      callListRef,
      getCallerInfo,
      findNextRingingCallHub,
      setPendingInvitationUuid,
      pendingInvitationUuid,
    ],
  )

  const handleIncomingInviteReceived = useCallback(
    ({
      callUuid,
      number,
      name,
      isClickToCall,
      isPageOrIntercom,
      queueId,
    }: {
      callUuid: string
      number: string
      name: string
      isClickToCall: boolean
      isPageOrIntercom?: boolean
      queueId?: string
    }) => {
      const isQueueCall = !!queueId

      logger?.info('Incoming invite received', {
        callUuid,
        number,
        name,
        isClickToCall,
        isPageOrIntercom,
        queueId,
        isQueueCall,
      })

      // Track that this call received a SIP INVITE (before any rejection logic)
      // This prevents WebSocket events from creating desk phone calls for softphone calls
      // even when browser throttling delays processing
      sipInviteReceivedRef.current.add(callUuid)
      logger?.debug('SIP invite tracked', {
        callUuid,
        trackedCount: sipInviteReceivedRef.current.size,
      })
      // Clean up after 30 seconds to prevent memory leaks
      setTimeout(() => {
        sipInviteReceivedRef.current.delete(callUuid)
      }, 30000)

      if (isClickToCall && !allowClickToCallIncoming) {
        const sipClient = getSipInstance()
        // Fire-and-forget: rejectCall always resolves, never rejects
        if (sipClient) void sipClient.rejectCall(callUuid)
        logger?.info('Invite auto-rejected', {
          callUuid,
          reason: 'Click-to-call',
        })
        return
      }

      // Check if second call should be rejected
      const shouldReject = checkSecondCallRejection({
        callList: callListRef.current,
        callUuid,
        hasCallWaiting,
        isQueueCall,
        isInQueue,
        source: 'sip',
        logger,
        extraLogData: { queueId },
        sipClient: getSipInstance(),
        removeCall,
      })

      if (shouldReject) {
        return
      }

      // We're going to ring this call. Send the 180 Ringing ourselves — the SIP
      // stack no longer auto-sends it (sendInitialProvisionalResponse: false), so
      // rejected calls never leak a 180 before their 486 (CUS-591). Fire-and-forget:
      // progressCall always resolves and is a no-op if the session isn't ringable.
      void getSipInstance()?.progressCall(callUuid)

      // Check if call already exists (e.g., from websocket arriving first)
      // If it does, preserve websocket contactInfo and other fields
      // Use ref to get latest callList state and avoid race conditions
      const existingCall = callListRef.current.get(callUuid)

      // If call already exists and is already RINGING or beyond, preserve that status
      // For new inbound calls, set to RINGING (not INITIAL - that's for outbound calls)
      const shouldPreserveStatus =
        existingCall && existingCall.status !== PhoneCallStatus.INITIAL
      const newStatus = shouldPreserveStatus
        ? existingCall.status
        : PhoneCallStatus.RINGING

      logger?.info('Adding call to callList', {
        callUuid,
        number,
        newStatus,
        existingCall: !!existingCall,
        hasContactInfo: !!existingCall?.contactInfo,
        isPageOrIntercom,
      })

      if (existingCall) {
        logger?.info('SIP handler preserving WebSocket data', {
          callUuid,
          existingCallSource: existingCall.callSource,
          hasContactInfo: !!existingCall.contactInfo,
        })
      }

      upsertCall({
        status: newStatus,
        callUuid: callUuid,
        number: number,
        name: name ?? null,
        startTime: existingCall?.startTime ?? new Date(), // Preserve existing startTime if available
        isOnHold: existingCall?.isOnHold ?? false,
        isMuted: existingCall?.isMuted ?? false,
        callSource: 'softphone',
        direction: 'inbound',
        isPageOrIntercom: isPageOrIntercom ?? false,

        // Store original SIP data
        sipCallerId: { number, name: name ?? null },

        // Store WebSocket contactInfo
        contactInfo: existingCall?.contactInfo ?? undefined,
      })

      if (existingCall && existingCall.contactInfo) {
        logger?.info('SIP handler race condition resolved', {
          callUuid,
          finalSource: 'softphone',
          contactInfoPreserved: true,
        })
      }

      // FIFO queue logic: set as pending if no current pending OR current pending is no longer ringing
      // Use ref to get latest callList state and avoid race conditions
      const currentPendingCall = pendingInvitationUuid
        ? callListRef.current.get(pendingInvitationUuid)
        : null
      const currentPendingIsRinging = currentPendingCall
        ? isPreConnectedStatus(currentPendingCall.status)
        : false

      if (!pendingInvitationUuid || !currentPendingIsRinging) {
        setPendingInvitationUuid(callUuid)
      }
    },
    [
      pendingInvitation,
      allowClickToCallIncoming,
      getSipInstance,
      pendingInvitationUuid,
      setPendingInvitationUuid,
      hasCallWaiting,
      isInQueue,
    ],
  )
  const handleTransportConnected = useCallback(() => {
    setTransportDropReported(false)
    setTransportConnectionState(TransportConnectionState.CONNECTED)
  }, [])
  const handleTransportDisconnected = useCallback(
    (error?: Error | undefined) => {
      setTransportDropReported(true)
      setTransportConnectionState(TransportConnectionState.DISCONNECTED)
      // getPhoneStatus reports Initial ahead of a disconnect, so a client that
      // never connected would otherwise stay on the initializing view.
      setRegistrationState((state) =>
        state === RegistererState.Initial ? RegistererState.Terminated : state,
      )
      if (error) {
        setRegistrationError(error)
      }
    },
    [],
  )
  const handleRegistrationRejected = useCallback(
    (errorMessage: string | undefined) => {
      logger?.info(`Registration rejected: ${errorMessage}`)
      setRegistrationError(new Error(errorMessage))
    },
    [],
  )
  const handleParkingSubscriptionUpdate = useCallback(
    ({ subscribedNumber, state }: PresenceStateChange) => {
      const matchingLot = trackedParkingLots.list.find(
        (lot) => lot.targetNumber === subscribedNumber,
      )
      if (matchingLot) {
        updateTrackedParkingLot({ id: matchingLot.id, presenceState: state })
      }
    },
    [trackedParkingLots.list],
  )
  const handleExtensionSubscriptionUpdate = useCallback(
    ({ subscribedNumber, state }: PresenceStateChange) => {
      const matchingExtension = trackedExtensions.list.find(
        (ext) => ext.uuid === subscribedNumber,
      )
      if (matchingExtension) {
        updateTrackedExtension({
          id: matchingExtension.id,
          presenceState: state,
        })
      }
    },
    [trackedExtensions.list],
  )
  // #endregion

  // Track SIP event handlers via refs for stable cleanup in SIP registration effect
  const handleSipClientRegistrationChangeRef = useRef(
    handleSipClientRegistrationChange,
  )
  const handleTransportConnectedRef = useRef(handleTransportConnected)
  const handleTransportDisconnectedRef = useRef(handleTransportDisconnected)
  const handleRegistrationRejectedRef = useRef(handleRegistrationRejected)
  useLayoutEffect(() => {
    handleSipClientRegistrationChangeRef.current =
      handleSipClientRegistrationChange
    handleTransportConnectedRef.current = handleTransportConnected
    handleTransportDisconnectedRef.current = handleTransportDisconnected
    handleRegistrationRejectedRef.current = handleRegistrationRejected
  }, [
    handleSipClientRegistrationChange,
    handleTransportConnected,
    handleTransportDisconnected,
    handleRegistrationRejected,
  ])

  // Register SIP client and attach registration listeners
  // Depends on changes to SIP connection info and flags to re-register
  useEffect(() => {
    if (!sipConn) return
    if (appVersion === APP_VERSION_PLACEHOLDER) return

    if (_sipClientInitializing) {
      logger?.info('SIP client initialization already in progress, skipping')
      return
    }

    // Capture handler refs - ensures on() and off() use identical references
    const onRegistrationChange = handleSipClientRegistrationChangeRef.current
    const onTransportConnected = handleTransportConnectedRef.current
    const onTransportDisconnected = handleTransportDisconnectedRef.current
    const onRegistrationRejected = handleRegistrationRejectedRef.current

    let cancelled = false

    if (initialSetup || needsReregister) {
      void (async () => {
        if (cancelled) return
        if (_sipClientInitializing) return

        _sipClientInitializing = true

        try {
          const authState = getCurrentAuthState()
          let accessToken = authState.appSessionJwts?.access_token

          if (!accessToken || isAccessTokenExpired(accessToken)) {
            logger?.info(
              'Access token expired, attempting refresh before SIP init',
            )
            try {
              const apiClient = getMangoApiClient()
              const { connected: refreshed, reason } =
                await apiClient.auth.verifyConnection()
              if (cancelled) return
              const refreshedState = getCurrentAuthState()
              accessToken = refreshedState.appSessionJwts?.access_token
              if (
                !refreshed ||
                !accessToken ||
                isAccessTokenExpired(accessToken)
              ) {
                logger?.info(
                  `Cannot initialize SIP client: ${reason === 'unauthorized' ? 'unauthorized' : 'token refresh failed'}`,
                )
                return
              }
            } catch {
              logger?.info('Cannot initialize SIP client: token refresh error')
              return
            }
          }

          try {
            if (initialSetup) logger?.info('Initial SIP registration')
            if (needsReregister) logger?.info('SIP re-registration')

            const existingSipClient = getSipInstance()
            if (existingSipClient) {
              logger?.info(
                'Disposing existing SIP client before re-registration',
              )
              existingSipClient.dispose().catch((e: unknown) => {
                logger?.warn('Failed to dispose existing SIP client', {
                  error: e,
                })
              })
            }

            const newSipClient = new SipClient({
              sipConnectionInfo: sipConn,
              logger: sipLogger,
              analytics: new Analytics(AnalyticsNamespace.SIP_CLIENT),
              userAgentString: `${isElectron ? 'WebPhone(DesktopApp)' : 'WebPhone'}/${appVersion}`,
            })
            newSipClient.on('RegistrationStateChanged', onRegistrationChange)
            newSipClient.on('TransportConnected', onTransportConnected)
            newSipClient.on('TransportDisconnected', onTransportDisconnected)
            newSipClient.on('RegistrationRejected', onRegistrationRejected)

            activeSipClientRef.current = newSipClient
            setSipInstance(newSipClient)
            if (initialSetup) setInitialSetup(false)
            if (needsReregister) {
              setStaleCallSweepPending(true)
              setTimeout(() => {
                setNeedsReregister(false)
              }, 0)
            }
          } catch (error: unknown) {
            const err =
              error instanceof Error ? error : new Error(String(error))
            logger?.error('SIP client initialization failed', err, {
              extensionId: userExtensionId,
              pbxId,
              registrationServerCount:
                sipConn.registration_servers?.length ?? null,
            })
          }
        } finally {
          _sipClientInitializing = false
        }
      })()
    }

    return () => {
      cancelled = true

      const sipClient = getSipInstance()

      // Ignore stale SIP clients left in shared state by a previous mount.
      // Only the client created by this hook instance should reset status.
      if (sipClient && sipClient === activeSipClientRef.current) {
        logger?.debug('SIP de-registration')
        sipClient.off('RegistrationStateChanged', onRegistrationChange)
        sipClient.off('TransportConnected', onTransportConnected)
        sipClient.off('TransportDisconnected', onTransportDisconnected)
        sipClient.off('RegistrationRejected', onRegistrationRejected)

        sipClient.dispose().catch((e: unknown) => {
          logger?.warn('SIP de-registration dispose failed', { error: e })
        })

        // The listeners above are detached before dispose(), so the SIP
        // atoms would otherwise keep their last live values (Registered /
        // CONNECTED) after the webphone disconnects — leaving the status
        // panel and any other readers showing a stale "connected" state.
        // Reset them to the disconnected baseline here. A drop reported by
        // this client is stale once it is gone, as is its error.
        setRegistrationState(RegistererState.Terminated)
        setTransportConnectionState(TransportConnectionState.DISCONNECTED)
        setTransportDropReported(false)
        setRegistrationError(null)
        activeSipClientRef.current = null

        logger?.info('SIP de-registration success')
      }
      _sipClientInitializing = false
    }
  }, [sipConnectionInfoKey, initialSetup, needsReregister, appVersion])
  // Attach SIP call event listeners
  // Depends on changes to the callbacks
  useEffect(() => {
    const sipClient = getSipInstance()
    if (!sipClient) return
    sipClient.on('OutboundSessionStateChange', handleOutboundSessionStateChange)
    sipClient.on('OutboundCallProgress', handleOutboundCallProgress)
    sipClient.on('InboundSessionStateChange', handleInboundSessionStateChange)
    sipClient.on('IncomingInviteReceived', handleIncomingInviteReceived)
    sipClient.on('ParkingStateChanged', handleParkingSubscriptionUpdate)
    sipClient.on('ExtensionStateChanged', handleExtensionSubscriptionUpdate)

    return () => {
      sipClient.off(
        'OutboundSessionStateChange',
        handleOutboundSessionStateChange,
      )
      sipClient.off('OutboundCallProgress', handleOutboundCallProgress)
      sipClient.off(
        'InboundSessionStateChange',
        handleInboundSessionStateChange,
      )
      sipClient.off('IncomingInviteReceived', handleIncomingInviteReceived)
      sipClient.off('ParkingStateChanged', handleParkingSubscriptionUpdate)
      sipClient.off('ExtensionStateChanged', handleExtensionSubscriptionUpdate)
    }
  }, [
    handleOutboundSessionStateChange,
    handleOutboundCallProgress,
    handleInboundSessionStateChange,
    handleIncomingInviteReceived,
    handleParkingSubscriptionUpdate,
    handleExtensionSubscriptionUpdate,
  ])

  useBeforeunload((event) => {
    const sipClient = getSipInstance()
    const currentCallList = callListRef.current
    const ringingCalls = Array.from(currentCallList.values()).filter((call) =>
      isPreConnectedStatus(call.status),
    )
    const activeCalls = Array.from(currentCallList.values()).filter(
      (call) => call.status === PhoneCallStatus.CONNECTED,
    )

    logger?.warn('App unloading', {
      callListSize: currentCallList.size,
      ringingCallsCount: ringingCalls.length,
      activeCallsCount: activeCalls.length,
    })
    if (ringingCalls.length > 0) {
      logger?.warn('App closing with ringing calls still in callList', {
        ringingCallsCount: ringingCalls.length,
        ringingCalls: ringingCalls.map((call) => ({
          callUuid: call.callUuid,
          status: call.status,
          number: call.number,
          startTime: call.startTime,
        })),
      })
    }
    if (currentCallList.size > 0) {
      logger?.warn('App closing with calls still in callList', {
        callListSize: currentCallList.size,
        calls: Array.from(currentCallList.values()).map((call) => ({
          callUuid: call.callUuid,
          status: call.status,
          number: call.number,
          source: call.callSource,
        })),
      })
      // In Electron, the main process handles close prevention via before-quit handler
      // Only prevent close in web browsers
      if (!isElectron) {
        event.preventDefault()
      }
      if (!event.defaultPrevented) {
        sipClient?.dispose().catch(() => {})
      }
    } else {
      sipClient?.dispose().catch(() => {})
    }
  })

  // Handle audio input device changes
  const hasActiveCall = !!activeCall
  const hasConferencedCalls = !!conferencedCalls

  useEffect(() => {
    if (currentAudioInput !== selectedInputDeviceId) {
      if (hasActiveCall || hasConferencedCalls) {
        setNeedsAudioConnected(true)
      }
      setCurrentAudioInput(selectedInputDeviceId)
    }
  }, [
    currentAudioInput,
    selectedInputDeviceId,
    hasActiveCall,
    hasConferencedCalls,
  ])

  // Handle audio output device changes
  useEffect(() => {
    if (currentAudioOutput !== selectedOutputDeviceId) {
      // Output-device changes are the classic trigger for the macOS CoreAudio
      // sample-rate renegotiation that used to desync the call AudioContext.
      // Log them so a choppy-audio report can be correlated with a device
      // switch even without a quality-summary event.
      // Read call state from refs so this effect's deps stay scoped to the
      // device IDs — the log just captures call state at the moment of a switch.
      logger?.info('Audio output device change', {
        from: currentAudioOutput,
        to: selectedOutputDeviceId,
        hasActiveCall: !!activeCallRef.current,
        hasConferencedCalls: !!conferencedCallsRef.current,
      })
      const audioElement = document.getElementById(audioElementId) as any
      if (audioElement) {
        if ('setSinkId' in HTMLAudioElement.prototype) {
          Promise.resolve(audioElement.setSinkId(selectedOutputDeviceId))
            .then(() => {
              setCurrentAudioOutput(selectedOutputDeviceId)
            })
            .catch((error) => {
              const domError = error as DOMException
              if (domError?.name === 'NotAllowedError') {
                return
              }
              // Handle NotFoundError when requested device no longer exists
              if (domError?.name === 'NotFoundError') {
                logger?.warn(
                  'Audio output device not found, falling back to default',
                  {
                    selectedOutputDeviceId,
                    note: 'Preference preserved for automatic reconnection',
                  },
                )
                return
              }
              logger?.debug('Audio output change failed.', error)
            })
          return
        }
        setCurrentAudioOutput(selectedOutputDeviceId)
      }
    }
  }, [currentAudioOutput, selectedOutputDeviceId, audioElementId])

  // Handle audio connection needs
  // Guard ref prevents re-entrant calls during async connectAudio
  const connectAudioInProgress = useRef(false)

  useEffect(() => {
    if (needsAudioConnected && !connectAudioInProgress.current) {
      const sipClient = getSipInstance()
      const clientAudioStream = getCallAudioStream(CLIENT_AUDIO_STREAM_KEY)
      // Read from refs rather than closure to guarantee the latest values
      // when this effect re-runs via needsAudioConnected
      const currentActiveCall = activeCallRef.current
      const currentConferencedCalls = conferencedCallsRef.current

      if (sipClient && currentActiveCall && clientAudioStream) {
        // Capture activeCallUuid at start to detect if user switched calls during async operation
        const targetCallUuid = currentActiveCall.callUuid

        if (
          currentConferencedCalls &&
          currentConferencedCalls
            .map((call) => getCallAudioStream(call.callUuid))
            .every(Boolean)
        ) {
          const callUuids = currentConferencedCalls.map((call) => call.callUuid)
          connectAudioInProgress.current = true
          connectAudio(sipClient, callUuids, conferenceIsMuted)
            .then(() => {
              // Only clear needsAudioConnected if we're still on the same call
              if (activeCallUuidRef.current === targetCallUuid) {
                setNeedsAudioConnected(false)
              }
            })
            .catch((e: unknown) => {
              logger?.error(
                'connectAudio (conference) failed',
                e instanceof Error ? e : new Error(String(e)),
              )
            })
            .finally(() => {
              connectAudioInProgress.current = false
            })
        } else if (getCallAudioStream(currentActiveCall.callUuid)) {
          connectAudioInProgress.current = true
          // isMuted read from ref is intentionally not a dependency --
          // mute toggling is handled separately; this only fires for audio reconnection.
          connectAudio(
            sipClient,
            [currentActiveCall.callUuid],
            currentActiveCall.isMuted,
          )
            .then(() => {
              // Only clear needsAudioConnected if we're still on the same call
              if (activeCallUuidRef.current === targetCallUuid) {
                setNeedsAudioConnected(false)
              }
            })
            .catch((e: unknown) => {
              logger?.error(
                'connectAudio failed',
                e instanceof Error ? e : new Error(String(e)),
              )
            })
            .finally(() => {
              connectAudioInProgress.current = false
            })
        }
      }
    }
  }, [
    needsAudioConnected,
    getSipInstance,
    getCallAudioStream,
    activeCall?.callUuid,
    conferencedCalls?.length,
    conferenceIsMuted,
  ])

  const updatePresenceSubscriptions = (
    parkingLots: ParkingLot[],
    extensions: Extension[],
  ) => {
    const sipClient = getSipInstance()
    if (!sipClient) return
    parkingLots.forEach((lot) => {
      sipClient.subscribeToParking(lot.targetNumber)
    })
    extensions.forEach((ext) => {
      sipClient.subscribeToExtension(ext.uuid)
    })
  }

  const lotsToUpdate = useMemo(
    () =>
      trackedParkingLots.withPresence.filter(
        (lot) =>
          lot.presence === undefined || lot.presence === PresenceState.Unknown,
      ),
    [trackedParkingLots.withPresence],
  )
  const extensionsToUpdate = useMemo(
    () =>
      trackedExtensions.withPresence.filter(
        (ext) =>
          ext.presence === undefined || ext.presence === PresenceState.Unknown,
      ),
    [trackedExtensions.withPresence],
  )
  // Handle presence subscription updates
  useEffect(() => {
    if (
      registrationState === RegistererState.Registered &&
      transportConnectionState === TransportConnectionState.CONNECTED &&
      (lotsToUpdate.length > 0 || extensionsToUpdate.length > 0)
    ) {
      updatePresenceSubscriptions(lotsToUpdate, extensionsToUpdate)
    }
  }, [
    registrationState,
    transportConnectionState,
    lotsToUpdate.length,
    extensionsToUpdate.length,
  ])

  // Set up resubscribe interval for presence updates, every 120 seconds
  // Depends on changes to the tracked parking lots and extensions
  useEffect(() => {
    const interval = setInterval(() => {
      // Only attempt subscription refresh if connected
      if (
        registrationState === RegistererState.Registered &&
        transportConnectionState === TransportConnectionState.CONNECTED
      ) {
        updatePresenceSubscriptions(
          trackedParkingLots.list,
          trackedExtensions.list,
        )
      }
    }, PRESENCE_SUBSCRIPTION_REFRESH_INTERVAL)

    return () => {
      clearInterval(interval)
    }
  }, [
    trackedParkingLots.list,
    trackedExtensions.list,
    getSipInstance,
    registrationState,
    transportConnectionState,
  ])
}

export interface PhoneEndpoint {
  state: {
    phoneStatus: PhoneStatus
    phoneCalls: {
      active: { activeCall: PhoneCall | undefined }
      list: { callList: Map<string, PhoneCall> }
      conference: ConferenceStateResponse
      invite: {
        pendingInvitation: PhoneCall | undefined
        pendingInvitationUuid: string | null
      }
      isAtMaxCallLimit: boolean
    }
    extensions: ExtensionPreview[]
    trackedExtensions: TrackedExtension[]
    trackedParkingLots: TrackedParkingLot[]
    registrationError: Error | null
    audioDevices: {
      output: {
        devices: MediaDeviceInfo[]
        preference: string
      }
      input: {
        devices: MediaDeviceInfo[]
        preference: string
      }
    }
    speedDial: SpeedDialState
  }
  actions: {
    switchCall: (callUuid: string) => Promise<void>
    startCall: (
      destination:
        string | { contactNumber: ContactNumber; contact: ContactDetails },
      outboundCallerIdName?: string,
    ) => Promise<void>
    endCall: (callUuid: string) => void
    dismissCall: (callUuid: string) => void
    endAllSoftphoneCalls: () => void
    answerCall: (callUuid?: string) => Promise<void>
    answerCallWithHoldCurrent: (callUuid?: string) => Promise<void>
    answerCallWithEndCurrent: (callUuid?: string) => Promise<void>
    answerCallWithParkCurrent: (
      parkTarget: string,
      callUuid?: string,
    ) => Promise<void>
    rejectCall: (callUuid?: string) => void
    rejectCallWithBusy: (callUuid?: string) => void
    /**
     * Toggle the mute state of a call
     * @param callUuid: The UUID of the call to mute
     * @param muted: undefined to toggle, true to mute, false to unmute
     */
    muteCall: (callUuid: string, muted?: boolean) => void
    /**
     * Toggle the hold state of a call
     * @param callUuid: The UUID of the call to hold
     * @param hold: undefined to toggle, true to hold, false to unhold
     */
    holdCall: (callUuid: string, hold?: boolean) => Promise<void>
    attendedTransferCall: (
      callUuid: string,
      targetCallUuid: string,
    ) => Promise<void>
    transferCall: (callUuid: string, target: string) => Promise<void>
    parkCall: (callUuid: string, parkTarget: string) => Promise<void>
    unparkCall: (parkId: string) => void
    mergeCalls: (callUuids: string[]) => void
    splitCalls: () => void
    endConferenceCall: () => void
    toggleMuteConference: () => void
    toggleHoldConference: () => Promise<void>
    sendDtmf: (callUuid: string, digit: string) => void
    resetConnection: () => void
    switchAudioOutput: (deviceId: string) => void
    switchAudioInput: (deviceId: string) => void
  }
}

export interface UsePhoneEndpointParams {
  onCallEnd?: () => void
  onCallStart?: () => void
  onFinalCallEnd?: () => void
}

export const usePhoneEndpoint = ({
  onCallEnd,
  onCallStart,
  onFinalCallEnd,
}: UsePhoneEndpointParams): PhoneEndpoint => {
  const announceCallFailure = useAnnounceCallFailure()
  const [, setNeedsAudioConnected] = useAtom(needsAudioConnectedAtom)
  const [, setNeedsClientAudioInput] = useAtom(needsClientAudioInputAtom)
  const setNeedsAudioCleanupCheck = useSetAtom(needsAudioCleanupCheckAtom)
  const removeCallAudioStream = useRemoveCallAudioStream()
  const [, setConnectionRefreshRequested] = useAtom(
    connectionRefreshRequestedAtom,
  )
  const [registrationState] = useAtom(registrationStatusAtom)
  const [registrationError] = useAtom(registrationErrorAtom)
  const [transportConnectionState] = useAtom(transportConnectionStateAtom)

  const [needsFinalCallEnd, setNeedsFinalCallEnd] = useState(false)

  const { track } = useAnalytics(AnalyticsNamespace.WEB_PHONE)
  const sipcAnalytics = useMemo(
    () => new Analytics(AnalyticsNamespace.SIP_CLIENT),
    [],
  )
  const deskphoneLifecycleRef = useRef(new DeskphoneCallLifecycleTracker())
  const { emit } = useEmitterService()
  const { logger } = useCloudwatchLogger('Webphone')

  const { pbxId, currentUserExtension, pbxExtensions, pbx } = useUserState()
  const effectiveCallPopEnabled = useCallPopEnabled()

  const {
    settings: {
      webPhone: {
        doNotDisturbTimestamp: endpointDoNotDisturbTimestamp,
        hasCallWaiting,
      },
    },
  } = useSettingsState()
  const endpointDoNotDisturb: boolean =
    endpointDoNotDisturbTimestamp !== undefined

  const { currentAgentStatus } = useCallQueue()
  const isInQueue = currentAgentStatus === QueueAgentStatus.InQueue

  const {
    sipInstance: [getSipInstance],
  } = useSipInstance()
  const sipClient = getSipInstance()

  useOnLogout(() => {
    if (sipClient?.isConnected) {
      sipClient?.dispose().catch(() => {})
    }
  }, 'usePhoneEndpoint')

  const {
    phoneStatus,
    phoneCalls: {
      active: { activeCall, setActiveCallUuid },
      list: { callList: rawCallList, upsertCall, removeCall },
      conference: {
        conferencedCalls,
        startTime: conferenceStartTime,
        isMuted: conferenceIsMuted,
        isOnHold: conferenceIsOnHold,
        addCalls: addCallsToConference,
        end: endConference,
        setMute: setConferenceMute,
        setHold: setConferenceHold,
      },
      invite: {
        pendingInvitation,
        pendingInvitationUuid,
        setPendingInvitationUuid,
      },
    },
    trackedParkingLots,
    trackedExtensions,
    pbxExtensionsList,
    audio: {
      output: {
        devices: outputs,
        preference: [selectedOutputDeviceId, setSelectectedOutputDeviceId],
      },
      input: {
        devices: inputs,
        preference: [selectedInputDeviceId, setSelectedInputDeviceId],
      },
    },
    speedDial,
  } = useWebphoneState({
    pbxId: pbxId ?? 0,
    registrationState,
    transportConnectionState,
  })

  // Keep a ref to the latest rawCallList to avoid stale closure issues in WebSocket handlers
  // This prevents race conditions where WebSocket events arrive before React state updates
  // Use useLayoutEffect to ensure synchronous update before any effects run
  const callListRef = useRef(rawCallList)
  useLayoutEffect(() => {
    callListRef.current = rawCallList
  }, [rawCallList])

  // Track call UUIDs that received a SIP INVITE (either processed or rejected)
  // These should not be created as desk phone calls when WebSocket events arrive
  // This prevents race conditions when browser throttling delays SIP invite processing
  const sipInviteReceivedRef = useRef<Set<string>>(new Set())

  const { upsertRecentCall } = useRecentCalls(pbxId, pbxExtensionsList ?? [])

  // Share callList and phone state via IPC when call pop is enabled (for Electron popup window)
  const [, setSharedCallList] = useSharedVariable<PhoneCall[]>('CallList', [])
  const [, setSharedActiveCallUuid] = useSharedVariable<string | null>(
    'ActiveCallUuid',
    null,
  )
  const [, setSharedPendingInvitationUuid] = useSharedVariable<string | null>(
    'PendingInvitationUuid',
    null,
  )
  const [, setSharedIsAtMaxCallLimit] = useSharedVariable<boolean>(
    'IsAtMaxCallLimit',
    false,
  )
  // Tells the call-pop that ringing calls are left out of CallList (Do Not
  // Disturb), so a ringing call disappearing from it doesn't mean it ended.
  const [, setSharedCallListHidesRinging] = useSharedVariable<boolean>(
    'CallListHidesRinging',
    false,
  )

  // Convert Map to array sorted by startTime (oldest first) for popup display
  // Filter out ringing calls when DND is enabled so they never reach Electron's popup visibility logic
  const callListArray = useMemo(() => {
    const allCalls = Array.from(rawCallList.values()).sort((a, b) => {
      // Sort by startTime (oldest first), with null startTimes at the end
      const timeA = a.startTime?.getTime() ?? Infinity
      const timeB = b.startTime?.getTime() ?? Infinity
      return timeA - timeB
    })

    // Filter out ringing calls when DND is enabled
    if (endpointDoNotDisturb) {
      return allCalls.filter((call) => !isPreConnectedStatus(call.status))
    }

    return allCalls
  }, [rawCallList, endpointDoNotDisturb])

  // Track serialized call data to detect actual changes
  const previousCallListSerializedRef = useRef<string>('')
  const previousCallListHidesRingingRef = useRef<boolean | null>(null)

  useEffect(() => {
    if (isElectron) {
      // Published before the list it describes, so the popup never sees a
      // ringing call vanish without knowing why.
      if (previousCallListHidesRingingRef.current !== endpointDoNotDisturb) {
        previousCallListHidesRingingRef.current = endpointDoNotDisturb
        setSharedCallListHidesRinging(endpointDoNotDisturb)
      }

      // Create a stable serialization of only the fields that affect popup rendering
      // Sort by UUID for consistent comparison and exclude startTime (changes frequently but doesn't affect display immediately)
      const callDataForComparison = callListArray
        .map((call) => ({
          uuid: call.callUuid,
          status: call.status,
          hold: call.isOnHold,
          muted: call.isMuted,
          name: call.name || '',
          number: call.number || '',
          source: call.callSource,
          direction: call.direction,
          isUnparked: call.isUnparked,
          // Include contactInfo to detect when WebSocket enriches call data
          hasContactInfo: !!call.contactInfo,
          contactExternalId: call.contactInfo?.contact_external_id || '',
        }))
        .sort((a, b) => a.uuid.localeCompare(b.uuid))

      const serialized = JSON.stringify(callDataForComparison)

      // Only update if call data actually changed
      if (serialized !== previousCallListSerializedRef.current) {
        previousCallListSerializedRef.current = serialized
        setSharedCallList(callListArray)
      }
    }
  }, [
    callListArray,
    endpointDoNotDisturb,
    setSharedCallList,
    setSharedCallListHidesRinging,
  ])

  const activeCallCount = Array.from(rawCallList.values()).filter(
    (call) => call.status !== PhoneCallStatus.INITIAL,
  ).length
  const isAtMaxCallLimit = activeCallCount >= MAX_CALLS

  // Share other phone state needed by Popup
  useEffect(() => {
    if (effectiveCallPopEnabled && isElectron) {
      setSharedActiveCallUuid(activeCall?.callUuid ?? null)
      setSharedPendingInvitationUuid(pendingInvitationUuid)
      setSharedIsAtMaxCallLimit(isAtMaxCallLimit)
    }
  }, [
    effectiveCallPopEnabled,
    activeCall?.callUuid,
    pendingInvitationUuid,
    isAtMaxCallLimit,
    setSharedActiveCallUuid,
    setSharedPendingInvitationUuid,
    setSharedIsAtMaxCallLimit,
  ])

  // Helper function to find next ringing call for FIFO queue
  const findNextRingingCall = useCallback(
    (excludeCallUuid?: string) => {
      const ringingCalls = Array.from(rawCallList.values())
        .filter(
          (call) =>
            call.callUuid !== excludeCallUuid &&
            isPreConnectedStatus(call.status),
        )
        .sort(
          (a, b) =>
            (a.startTime?.getTime() ?? 0) - (b.startTime?.getTime() ?? 0),
        )

      return ringingCalls[0]
    },
    [rawCallList],
  )

  const { subscribe: wssSubscribe } = useWebSocketService()

  // Helper to check if desk phone call should be handled by this user
  const shouldHandleDeskPhoneCall = useCallback(
    (messageExtension: string, ccQueue?: string, ccAgent?: string) => {
      const userExtensionNumbers = pbxExtensions?.map((ext) => ext.number) || []
      if (currentUserExtension?.number) {
        userExtensionNumbers.push(currentUserExtension.number)
      }

      const extensionStr = String(messageExtension || '')
      const queueStr = String(ccQueue || '')

      // Extract agent UUID from cc_agent (format: "uuid@domain")
      const agentUuid = ccAgent ? ccAgent.split('@')[0] : ''

      // Collect all user extension UUIDs
      const userExtensionUuids = pbxExtensions?.map((ext) => ext.uuid) || []
      if (currentUserExtension?.uuid) {
        userExtensionUuids.push(currentUserExtension.uuid)
      }

      // For queue calls, verify the agent UUID matches any of the user's extensions
      const isQueueCall =
        queueStr &&
        extensionStr === '' &&
        agentUuid &&
        userExtensionUuids.includes(agentUuid)

      const extensionMatches = userExtensionNumbers.some(
        (num) => String(num) === extensionStr,
      )

      return (
        isQueueCall || (userExtensionNumbers.length > 0 && extensionMatches)
      )
    },
    [pbxExtensions, currentUserExtension],
  )

  const handleWebSocketDeskPhoneRinging = useCallback(
    (message: WebSocketEvent<WebSocketEventType.CallRinging>) => {
      // Ignore deskphone calls when popup is disabled
      if (!effectiveCallPopEnabled) {
        logger?.debug('WebSocket RINGING ignored - Call Pop disabled')
        return
      }

      const callUuid = message.payload?.call_uuid
      const messageExtension = message.payload.extension_number

      if (!callUuid) return
      if (message.payload.mango_direction !== 'inbound') return

      // Identify queue calls: have cc_queue AND empty extension_number
      const isQueueCall = !!(
        message.payload.cc_queue && messageExtension === ''
      )

      // If SIP session exists, this is a softphone call (queue call) - let SIP INVITE handler manage it
      // WebSocket will only enrich with contactInfo, not create the call
      const hasSipSession = sipClient?.hasSession(callUuid) ?? false

      const shouldHandle = shouldHandleDeskPhoneCall(
        messageExtension,
        message.payload.cc_queue,
        message.payload.cc_agent,
      )

      logger?.debug('WebSocket RINGING shouldHandle check', {
        callUuid,
        messageExtension,
        cc_queue: message.payload.cc_queue,
        cc_agent: message.payload.cc_agent,
        shouldHandle,
        currentUserExtension: currentUserExtension?.number,
      })

      if (!shouldHandle) {
        logger?.debug(
          'WebSocket RINGING ignored - shouldHandleDeskPhoneCall returned false',
        )
        return
      }

      // Check if second call should be rejected
      const shouldReject = checkSecondCallRejection({
        callList: callListRef.current,
        callUuid,
        hasCallWaiting,
        isQueueCall,
        isInQueue,
        excludeCallUuid: callUuid,
        source: 'websocket',
        logger,
        sipClient: hasSipSession ? sipClient : null,
        removeCall,
      })

      if (shouldReject) {
        return
      }

      // Check if call already exists in callList (from SIP INVITE)
      const existingCall = callListRef.current.get(callUuid)

      const contact = getContactFromWebSocketMessage(
        message.payload,
        pbx?.features?.is_grove ?? false,
      )
      const contactName =
        getContactFullName(contact) ??
        message.payload?.caller_id_name ??
        'Unknown Caller'

      if (existingCall) {
        // SIP INVITE already created this call as 'softphone' - just enrich with contactInfo
        // Preserve the existing callSource (should be 'softphone' from SIP)
        const hasContactInfo = !!contact
        const needsStatusUpdate =
          existingCall.status === PhoneCallStatus.INITIAL
        const shouldUpdate = needsStatusUpdate || hasContactInfo

        if (shouldUpdate) {
          const newContactInfo = hasContactInfo
            ? contact
            : existingCall.contactInfo

          logger?.info('Enriching SIP call with WebSocket contactInfo', {
            callUuid,
            currentStatus: existingCall.status,
            callSource: existingCall.callSource,
            hasContactInfo,
            needsStatusUpdate,
          })
          upsertCall({
            ...existingCall,
            // Don't overwrite number/name - keep SIP data for backward compatibility
            // Components will use getDisplayCallerInfo() to choose which data to display
            status: needsStatusUpdate
              ? PhoneCallStatus.RINGING
              : existingCall.status,
            contactInfo: newContactInfo,
            // Explicitly preserve callSource from SIP to prevent race condition override
            callSource: existingCall.callSource,
            // Explicitly preserve sipCallerId from SIP
            sipCallerId: existingCall.sipCallerId,
          })
        }
        return
      }

      // No existing call in callList - this is either:
      // 1. True desk phone call (no SIP session exists)
      // 2. Race condition where WebSocket arrived before SIP INVITE handler completed (SIP session exists)
      // We already checked hasSipSession at the top of this function

      // Check if SIP invite was received (even if handler hasn't completed yet due to throttling)
      const sipInviteReceived = sipInviteReceivedRef.current.has(callUuid)

      if (hasSipSession || sipInviteReceived) {
        const reason = hasSipSession
          ? 'SIP session exists'
          : 'SIP invite received (handler delayed by throttling)'
        logger?.info('WebSocket creating softphone call', {
          callUuid,
          hasSipSession,
          sipInviteReceived,
          reason,
        })
        // SIP session exists OR SIP invite was received (but handler not complete yet due to throttling)
        // Either way, this is a softphone call - create it with contact info
      } else {
        logger?.info('WebSocket creating desk phone call', {
          callUuid,
          hasSipSession,
          sipInviteReceived,
        })
      }

      // Don't add calls when DND is enabled
      if (endpointDoNotDisturb) {
        logger?.info('Ignoring desk phone call - Do Not Disturb enabled', {
          callUuid,
        })
        return
      }

      if (isCallDismissed(callUuid)) {
        logger?.debug('WebSocket RINGING ignored - call was dismissed', {
          callUuid,
        })
        return
      }

      // Determine callSource based on SIP session existence OR SIP invite received
      // This handles browser throttling where SIP invite handler hasn't completed yet
      const callSource =
        hasSipSession || sipInviteReceived ? 'softphone' : 'deskphone'
      upsertCall({
        status: PhoneCallStatus.RINGING,
        callUuid: callUuid,
        number: message.payload._mango_phoneNumber || 'Unknown',
        name: contactName,
        destinationNumber: message.payload.mango_destination,
        startTime: new Date(),
        isOnHold: false,
        isMuted: false,
        callSource,
        direction: message.payload.mango_direction,
        contactInfo: contact,
        extensionNumber: message.payload.extension_number,
        extensionName: message.payload.extension_name,
      })

      // Log the final state after creation
      logger?.info('WebSocket call created', {
        callUuid,
        callSource,
        number: message.payload._mango_phoneNumber || 'Unknown',
        hasContactInfo: !!contact,
        hasSipSession,
        sipInviteReceived,
      })
    },
    [
      upsertCall,
      removeCall,
      shouldHandleDeskPhoneCall,
      logger,
      endpointDoNotDisturb,
      effectiveCallPopEnabled,
      sipClient,
      hasCallWaiting,
      isInQueue,
      pbx,
    ],
  )

  const handleWebSocketDeskPhoneAnswered = useCallback(
    (message: WebSocketEvent<WebSocketEventType.CallAnswered>) => {
      const callUuid = message.payload.call_uuid
      const messageExtension = message.payload.extension_number

      if (!callUuid) return

      const shouldHandle = shouldHandleDeskPhoneCall(
        messageExtension,
        message.payload.cc_queue,
        message.payload.cc_agent,
      )
      const hasSipSession = sipClient?.hasSession(callUuid) ?? false
      const hadRecentSipSession =
        sipClient?.hadRecentSipSession?.(callUuid) ?? false
      const call = callListRef.current.get(callUuid)

      if (
        shouldEmitDeskphoneLifecycle({
          shouldHandle,
          hasSipSession,
          hadRecentSipSession,
          callSource: call?.callSource,
        })
      ) {
        const emission = deskphoneLifecycleRef.current.onConnected({
          callUuid,
          direction: message.payload.mango_direction,
          startedAtMs: call?.startTime?.getTime(),
        })
        if (emission) {
          sipcAnalytics.track(
            emission.event,
            toAnalyticsData(emission.properties),
          )
        }
      }

      if (!effectiveCallPopEnabled) {
        return
      }

      if (!shouldHandle) {
        return
      }

      if (call && call.callSource === 'deskphone') {
        const previousStatus = call.status
        logger?.info('Desk phone call answered', {
          callUuid,
          previousStatus,
          newStatus: PhoneCallStatus.CONNECTED,
          number: call.number,
        })
        upsertCall({
          ...call,
          status: PhoneCallStatus.CONNECTED,
          startTime: call.startTime ?? new Date(),
        })
      } else {
        logger?.debug(
          'Desk phone answered event but call not found or not desk phone',
          {
            callUuid,
            callExists: !!call,
            callSource: call?.callSource,
          },
        )
      }
    },
    [
      upsertCall,
      shouldHandleDeskPhoneCall,
      effectiveCallPopEnabled,
      sipClient,
      sipcAnalytics,
    ],
  )

  const handleWebSocketDeskPhoneHangup = useCallback(
    (message: WebSocketEvent<WebSocketEventType.CallHangup>) => {
      const callUuid = message.payload.call_uuid
      const messageExtension = message.payload.extension_number

      if (!callUuid) return

      const shouldHandle = shouldHandleDeskPhoneCall(
        messageExtension,
        message.payload.cc_queue,
        message.payload.cc_agent,
      )
      const hasSipSession = sipClient?.hasSession(callUuid) ?? false
      const hadRecentSipSession =
        sipClient?.hadRecentSipSession?.(callUuid) ?? false
      const call = callListRef.current.get(callUuid)

      if (
        shouldEmitDeskphoneLifecycle({
          shouldHandle,
          hasSipSession,
          hadRecentSipSession,
          callSource: call?.callSource,
        })
      ) {
        const emission = deskphoneLifecycleRef.current.onStopped({
          callUuid,
          direction: message.payload.mango_direction,
          startedAtMs: call?.startTime?.getTime(),
        })
        if (emission) {
          sipcAnalytics.track(
            emission.event,
            toAnalyticsData(emission.properties),
          )
        }
      }

      if (!effectiveCallPopEnabled) {
        return
      }

      if (!shouldHandle) {
        return
      }

      logger?.info('WebSocket HANGUP', {
        callUuid,
        callExists: !!call,
        callSource: call?.callSource,
        callStatus: call?.status,
      })
      clearCallDismissed(callUuid)
      if (call && call.callSource === 'deskphone') {
        logger?.info('Desk phone call hangup, removing from callList', {
          callUuid,
        })
        removeCall(callUuid)
      }
    },
    [
      removeCall,
      shouldHandleDeskPhoneCall,
      effectiveCallPopEnabled,
      sipClient,
      sipcAnalytics,
    ],
  )

  // #region SIP event handlers
  const handleOnCallCallbacks = useCallback(
    (stateChange: SessionStateChange) => {
      switch (stateChange.state) {
        case SessionState.Establishing:
          break
        case SessionState.Established:
          onCallStart?.()
          break
        case SessionState.Terminating:
          break
        case SessionState.Terminated:
          onCallEnd?.()
          if (onFinalCallEnd) {
            setNeedsFinalCallEnd(true)
          }
          break
        default:
          break
      }
    },
    [onCallStart, onCallEnd, onFinalCallEnd],
  )

  useEffect(() => {
    if (!onCallStart && !onCallEnd && !onFinalCallEnd) return
    if (!sipClient) return
    logger?.debug('Phone endpoint listener registration')
    sipClient.on('OutboundSessionStateChange', handleOnCallCallbacks)
    sipClient.on('InboundSessionStateChange', handleOnCallCallbacks)

    return () => {
      sipClient.off('OutboundSessionStateChange', handleOnCallCallbacks)
      sipClient.off('InboundSessionStateChange', handleOnCallCallbacks)
    }
  }, [sipClient])
  // #endregion

  // ANSWERED/HANGUP stay subscribed with Call Pop off; RINGING does not.
  useEffect(() => {
    wssSubscribe(WebSocketEventType.CallAnswered, {
      subscriptionToken: 'usePhone::deskphone-answered',
      callback: handleWebSocketDeskPhoneAnswered,
    })

    wssSubscribe(WebSocketEventType.CallHangup, {
      subscriptionToken: 'usePhone::deskphone-hangup',
      callback: handleWebSocketDeskPhoneHangup,
    })

    if (!effectiveCallPopEnabled) {
      return
    }

    wssSubscribe(WebSocketEventType.CallRinging, {
      subscriptionToken: 'usePhone::deskphone-ringing',
      callback: handleWebSocketDeskPhoneRinging,
    })
  }, [
    wssSubscribe,
    handleWebSocketDeskPhoneRinging,
    handleWebSocketDeskPhoneAnswered,
    handleWebSocketDeskPhoneHangup,
    effectiveCallPopEnabled,
  ])

  // #region Actions
  const holdCallInternal = async (call: PhoneCall, hold: boolean) => {
    if (!sipClient) {
      logger?.warn('holdCallInternal called but SIP client not available', {
        callUuid: call.callUuid,
        hold,
      })
      return
    }

    // Desk phone calls can't be held via SIP - they're managed by the physical phone
    if (call.callSource === 'deskphone') {
      logger?.warn('Cannot hold desk phone call via SIP', {
        callUuid: call.callUuid,
      })
      return
    }

    if (call.status !== PhoneCallStatus.CONNECTED) {
      logger?.warn(
        'holdCallInternal: Call is not in CONNECTED state, skipping hold',
        {
          callUuid: call.callUuid,
          callStatus: call.status,
          hold,
        },
      )
      return
    }

    // Log call state before hold operation
    const previousHoldState = call.isOnHold
    const sipSessionExists = sipClient.hasSession(call.callUuid)

    logger?.info('holdCallInternal starting', {
      callUuid: call.callUuid,
      callStatus: call.status,
      previousHoldState,
      targetHoldState: hold,
      callNumber: call.number,
      callSource: call.callSource,
      sipSessionExists,
      isActiveCall: call.callUuid === activeCall?.callUuid,
    })

    // Verify SIP session exists before attempting hold
    if (!sipSessionExists) {
      logger?.warn(
        'holdCallInternal: SIP session does not exist for call - cleaning up stale call',
        {
          callUuid: call.callUuid,
          callStatus: call.status,
          hold,
          callNumber: call.number,
          callSource: call.callSource,
        },
      )
      // The call is in the callList as CONNECTED but has no SIP session (e.g. after
      // a network disruption or SIP transport reconnect that cleared sessions). No
      // Terminated event can arrive for it, so tear it down here the same way the SIP
      // Terminated handler would - leaving it in the list (even as ENDED) keeps it
      // counting against MAX_CALLS and blocks the user from placing new calls.
      upsertCall({ ...call, status: PhoneCallStatus.ENDED })
      removeCall(call.callUuid)
      removeCallAudioStream(call.callUuid)
      setNeedsAudioCleanupCheck(true)
      if (call.callUuid === activeCall?.callUuid) {
        const nextActiveCall = findNextActiveCall(
          callListRef.current,
          new Set([call.callUuid]),
        )
        setActiveCallUuid(nextActiveCall?.callUuid ?? null)
        if (
          nextActiveCall?.status === PhoneCallStatus.CONNECTED &&
          !nextActiveCall.isOnHold
        ) {
          setNeedsAudioConnected(true)
        }
      }
      return
    }

    // Concurrent hold/resume for the same call is serialized inside SipClient
    // (see _pendingHoldOperations) — the guard has to live there because every
    // mounted usePhoneEndpoint shares one client, and the reported race crosses
    // instances (popup IPC resume vs. in-window Hold button vs. headset).
    try {
      const inviteRequest = await sipClient.holdCall(call.callUuid, hold)

      // undefined means SipClient suppressed the re-INVITE. Skip the optimistic
      // isOnHold update so the UI keeps matching the SIP session — flipping it
      // here would desync the toggle and make the user's next click a no-op.
      if (!inviteRequest) {
        logger?.warn(
          'holdCallInternal: hold/resume already in flight for call, skipping',
          {
            callUuid: call.callUuid,
            previousHoldState,
            targetHoldState: hold,
          },
        )
        return
      }

      // Log successful hold operation
      logger?.info('holdCallInternal: SIP holdCall succeeded', {
        callUuid: call.callUuid,
        previousHoldState,
        newHoldState: hold,
        callStatus: call.status,
      })

      upsertCall({ ...call, isOnHold: hold })

      // When resuming a call from hold (hold = false), we need to reconnect audio
      // This is crucial for the scenario where:
      // 1. Call A is on hold
      // 2. Call B (active) ends
      // 3. Call A becomes active (but on hold, so audio isn't connected)
      // 4. User resumes Call A - audio must be reconnected
      // Note: This will trigger audio reconnection regardless of whether this is the
      // active call yet. The audio connection logic will only actually connect if
      // this call is the active call, so this is safe to call in all resume scenarios.
      if (!hold) {
        logger?.info('Resuming call from hold, requesting audio reconnection', {
          callUuid: call.callUuid,
        })
        setNeedsAudioConnected(true)
      }

      // Note: callList update propagates to popup automatically via IPC
    } catch (error: any) {
      // Overlapping re-INVITE (e.g. rapid hold/unhold): nothing hit the wire and
      // hold state is unchanged, so skip the failure handling. Already warned at
      // the SipClient.holdCall layer.
      if (error instanceof RequestPendingError) {
        return
      }

      announceCallFailure(
        t(
          hold
            ? 'callAnnouncements.holdFailed'
            : 'callAnnouncements.resumeFailed',
        ),
      )

      // Enhanced error logging with full context
      logger?.error('holdCallInternal: Hold operation failed', error, {
        callUuid: call.callUuid,
        callStatus: call.status,
        previousHoldState,
        targetHoldState: hold,
        callNumber: call.number,
        callSource: call.callSource,
        sipSessionExists,
        isActiveCall: call.callUuid === activeCall?.callUuid,
        errorMessage: error?.message,
        errorStack: error?.stack,
        // Check if call still exists after error
        callStillExists: rawCallList.has(call.callUuid),
        callStatusAfterError: rawCallList.get(call.callUuid)?.status,
      })

      // Check if the call was terminated as a result of the hold operation
      const callAfterError = rawCallList.get(call.callUuid)
      if (!callAfterError || callAfterError.status === PhoneCallStatus.ENDED) {
        logger?.error(
          'holdCallInternal: Call was terminated after hold operation failure - BUG DETECTED',
          error,
          {
            callUuid: call.callUuid,
            callStatusBefore: call.status,
            callStatusAfter: callAfterError?.status,
            hold,
            errorMessage: error?.message,
          },
        )
      }
    }
  }

  const holdCall = async (callUuid: string, hold?: boolean) => {
    const call = rawCallList.get(callUuid)
    if (!call) return

    const targetHoldState = hold === undefined ? !call.isOnHold : hold

    if (call.isOnHold === targetHoldState) {
      // Note: callList update propagates to popup automatically via IPC
      return
    }

    await holdCallInternal(call, targetHoldState)
  }

  const toggleHoldConference = async () => {
    if (!sipClient) return
    if (!conferencedCalls) return
    const holdStatus = !conferenceIsOnHold
    await Promise.all(
      conferencedCalls.map((call) => holdCallInternal(call, holdStatus)),
    )
    setConferenceHold(holdStatus)
  }

  const startCall = async (
    destination:
      string | { contactNumber: ContactNumber; contact: ContactDetails },
    outboundCallerIdName?: string,
    isUnparked?: boolean,
  ) => {
    // Check if SIP client is connected before attempting to start a call
    if (!sipClient?.isConnected()) {
      logger?.warn('Cannot start call - SIP client not connected')
      emit(NotificationEmitterEventType.PROCESS_UPDATED, {
        disposition: NotificationDisposition.Error,
        domain: NotificationDomain.WEBPHONE,
        operation: WebphoneOperation.CallUnavailable,
      })
      return
    }

    const isContactDestination = typeof destination !== 'string'
    const rawTarget = isContactDestination
      ? destination.contactNumber.number
      : destination
    let displayNameOverride = isContactDestination
      ? destination.contact.name
      : undefined

    // Resolves the `#` terminator before anything downstream sees the target.
    // Callers gate their call buttons on this too, so an empty result here means
    // an ungated entry point (a contact record, click-to-call) carried something
    // undialable - surface it instead of leaving a call stuck in INITIAL.
    // Keep this above the hold below, or an undialable target parks a live call
    // on its way to bailing out.
    const targetNumber = toDialTarget(rawTarget)
    const maskedTarget = toMaskedPhoneNumber(rawTarget)
    if (!targetNumber) {
      logger?.warn('Cannot start call - no dialable target', {
        target: maskedTarget,
      })
      emit(NotificationEmitterEventType.PROCESS_UPDATED, {
        disposition: NotificationDisposition.Error,
        domain: NotificationDomain.WEBPHONE,
        operation: WebphoneOperation.CallTargetUndialable,
        context: { target: maskedTarget },
      })
      return
    }

    // Find the truly active call (connected and not on hold) from fresh rawCallList
    const trulyActiveCall = Array.from(rawCallList.values()).find(
      (call) => call.status === PhoneCallStatus.CONNECTED && !call.isOnHold,
    )

    if (trulyActiveCall) {
      await holdCallInternal(trulyActiveCall, true)
      // Wait for React state to propagate
      await new Promise((resolve) => setTimeout(resolve, 100))
    }

    if (targetNumber.length <= 5) {
      if (
        targetNumber.length === 4 &&
        targetNumber.startsWith(PARKING_LOT_PREFIX_DIGITS)
      ) {
        const parkingLot = trackedParkingLots.list.find(
          (lot) => lot.targetNumber === targetNumber,
        )
        displayNameOverride = parkingLot?.label
      }
      const extension = pbxExtensionsList.find(
        (ext) => ext.number === targetNumber,
      )
      if (extension) {
        displayNameOverride = extension.name
      }
    }
    const newCallUuid = crypto.randomUUID()

    sipClient
      .startCall(
        newCallUuid,
        targetNumber,
        displayNameOverride,
        outboundCallerIdName,
      )
      .catch((e: unknown) => {
        logger?.error(
          'startCall failed',
          e instanceof Error ? e : new Error(String(e)),
        )
      })

    upsertCall({
      status: PhoneCallStatus.INITIAL,
      callUuid: newCallUuid,
      number: targetNumber,
      name: displayNameOverride ?? null,
      startTime: null,
      isOnHold: false,
      isMuted: false,
      callSource: 'softphone',
      direction: 'outbound',
      isUnparked: isUnparked ?? false,
    })
    setActiveCallUuid(newCallUuid)
    setNeedsClientAudioInput(true)
    upsertRecentCall(destination)
  }
  const endCallInternal = async (call: PhoneCall) => {
    if (!sipClient) return

    // Desk phone calls can't be ended via SIP - they're managed by WebSocket events
    if (call.callSource === 'deskphone') {
      logger?.warn('Cannot end desk phone call via SIP', {
        callUuid: call.callUuid,
      })
      return
    }

    try {
      sipClient.endCall(call.callUuid)
      // Wait a moment for the call to actually end
      await new Promise((resolve) => setTimeout(resolve, 500))
    } catch (e) {
      logger?.info('Error ending call', { callUuid: call.callUuid, error: e })
    }
  }

  const endCall = (callUuid: string) => {
    logger?.info('End call', {
      callUuid,
      callListSize: rawCallList.size,
    })
    if (!sipClient) {
      logger?.warn('End call: No SIP client available', { callUuid })
      return
    }

    const call = rawCallList.get(callUuid)
    if (
      !call ||
      call.status === PhoneCallStatus.ENDED ||
      call.status === PhoneCallStatus.ENDING_CALL
    ) {
      logger?.info('Call already ended or ending', {
        callUuid,
        status: call?.status,
      })
      return
    }

    logger?.info('Ending call', {
      callUuid,
      status: call.status,
      number: call.number,
      callSource: call.callSource,
    })

    // Desk phone calls can't be ended via SIP - they're managed by WebSocket events
    if (call.callSource === 'deskphone') {
      logger?.warn('Cannot end desk phone call via SIP', { callUuid })
      return
    }

    try {
      sipClient.endCall(callUuid)
      logger?.info('SIP endCall called successfully', { callUuid })
    } catch (e) {
      logger?.error('Error ending call', e as Error, {
        callUuid,
        error: e,
      })
    }
  }

  const endAllSoftphoneCalls = () => {
    logger?.info('Ending all softphone calls')

    // End all softphone calls (not deskphone)
    Array.from(rawCallList.values()).forEach((call) => {
      if (call.callSource === 'softphone') {
        endCall(call.callUuid)
      }
    })
  }

  const dismissCall = (callUuid: string) => {
    const call = rawCallList.get(callUuid)
    logger?.warn('Dismiss call requested (manual removal)', {
      callUuid,
      status: call?.status,
      callSource: call?.callSource,
      number: call?.number,
    })
    track(WebPhoneAnalyticsEvents.OrphanedCallCleanup, {
      layer: 'manual_dismiss',
      callSource: call?.callSource ?? 'unknown',
      callStatus: call?.status ?? 'unknown',
    })
    markCallDismissed(callUuid)
    removeCall(callUuid)

    if (callUuid === pendingInvitationUuid) {
      const nextRingingCall = findNextRingingCall(callUuid)
      setPendingInvitationUuid(nextRingingCall?.callUuid ?? null)
    }
  }

  const muteCallInternal = (call: PhoneCall, mute: boolean) => {
    if (!sipClient) return

    // Desk phone calls can't be muted via SIP - they're managed by the physical phone
    if (call.callSource === 'deskphone') {
      logger?.warn('Cannot mute desk phone call via SIP', {
        callUuid: call.callUuid,
      })
      return
    }

    sipClient.muteCall(call.callUuid, mute)
    upsertCall({ ...call, isMuted: mute })
  }

  const muteCall = (
    callUuid: string,
    muted: boolean | undefined = undefined,
  ) => {
    const call = rawCallList.get(callUuid)
    if (!call || call.isMuted === muted) return
    muteCallInternal(call, muted === undefined ? !call.isMuted : muted)
  }

  const toggleMuteConference = () => {
    if (!sipClient) return
    setConferenceMute(!conferenceIsMuted)
    setNeedsAudioConnected(true)
  }

  const attendedTransferCall = async (
    callUuid: string,
    targetCallUuid: string,
  ) => {
    if (!sipClient) return
    const call = rawCallList.get(callUuid)
    if (!call) return
    const targetCall = rawCallList.get(targetCallUuid)
    if (!targetCall || targetCall.status !== PhoneCallStatus.CONNECTED) return
    if (call.status !== PhoneCallStatus.CONNECTED) return

    // Desk phone calls can't be transferred via SIP - they're managed by the physical phone
    if (call.callSource === 'deskphone') {
      logger?.warn('Cannot transfer desk phone call via SIP', { callUuid })
      return
    }

    const transferContext = {
      callName: call.name || toMaskedPhoneNumber(call.number),
      transferToName: targetCall.name || toMaskedPhoneNumber(targetCall.number),
    }

    try {
      await sipClient.attendedTransferCall(callUuid, targetCallUuid)
      emit(NotificationEmitterEventType.PROCESS_UPDATED, {
        disposition: NotificationDisposition.Success,
        domain: NotificationDomain.WEBPHONE,
        operation: WebphoneOperation.CallTransferred,
        context: transferContext,
      })
      track(WebPhoneAnalyticsEvents.WarmTransfer)
    } catch (error) {
      logger?.error('Attended transfer failed', error as Error, {
        callUuid,
        targetCallUuid,
      })
      emit(NotificationEmitterEventType.PROCESS_UPDATED, {
        disposition: NotificationDisposition.Error,
        domain: NotificationDomain.WEBPHONE,
        operation: WebphoneOperation.CallTransferred,
        context: transferContext,
      })
      throw error
    }
  }

  const blindTransferCall = async (callUuid: string, target: string) => {
    if (!sipClient) {
      return
    }

    const call = rawCallList.get(callUuid)

    if (!call) {
      return
    }

    if (call.status !== PhoneCallStatus.CONNECTED) {
      return
    }

    // Desk phone calls can't be transferred via SIP - they're managed by the physical phone
    if (call.callSource === 'deskphone') {
      logger?.warn('Cannot transfer desk phone call via SIP', { callUuid })
      return
    }

    const isParking = target.startsWith(PARKING_LOT_PREFIX_STRING)

    const rawTarget = target
    if (!isParking) {
      target = toDialTarget(target)
    }
    const parkTarget = isParking ? target.split('+')[1] : undefined

    const callName = call.name || toMaskedPhoneNumber(call.number)
    const notificationBase = isParking
      ? {
          domain: NotificationDomain.WEBPHONE as const,
          operation: WebphoneOperation.CallParked as const,
          context: {
            callName,
            parkingLotName:
              trackedParkingLots.list.find(
                (lot) => lot.targetNumber === parkTarget,
              )?.label ?? parkTarget!,
          },
        }
      : {
          domain: NotificationDomain.WEBPHONE as const,
          operation: WebphoneOperation.CallTransferred as const,
          context: {
            callName,
            // Names the target the user asked for, which is all there is to show
            // when toDialTarget rejected it below.
            transferToName:
              pbxExtensionsList.find((ext) => ext.number === target)?.name ??
              toMaskedPhoneNumber(rawTarget),
          },
        }

    if (!target) {
      logger?.warn('Cannot transfer call - no dialable target', { callUuid })
      emit(NotificationEmitterEventType.PROCESS_UPDATED, {
        disposition: NotificationDisposition.Error,
        ...notificationBase,
      })
      return
    }

    try {
      await sipClient.blindTransferCall(callUuid, target)

      emit(NotificationEmitterEventType.PROCESS_UPDATED, {
        disposition: NotificationDisposition.Success,
        ...notificationBase,
      })
      if (!isParking) {
        track(WebPhoneAnalyticsEvents.ColdTransfer)
      }
    } catch (error) {
      if ((error as Error).message === SESSION_NOT_FOUND_ERROR_MESSAGE) {
        // Expected race condition: remote party hung up just before transfer/park was
        // initiated. The session is already gone, so this is not an actionable error.
        logger?.warn(
          'Blind transfer skipped: session already gone (race condition)',
          {
            callUuid,
            isParking,
            target: toMaskedPhoneNumber(target),
          },
        )

        // A CONNECTED call with no SIP session is not always a sub-second race, so
        // returning silently would leave a user who pressed Park believing it worked.
        // A dead call cannot be parked, so report the park failure; a transfer the
        // remote party already ended needs no notification.
        if (!isParking) {
          return
        }

        emit(NotificationEmitterEventType.PROCESS_UPDATED, {
          disposition: NotificationDisposition.Error,
          ...notificationBase,
        })
        return
      }

      if ((error as Error).message === USER_AGENT_NOT_CONNECTED_ERROR_MESSAGE) {
        // Signaling transport dropped between the click and the REFER. Nothing is
        // broken in our code, so it does not belong in Sentry as an error.
        logger?.warn(
          'Blind transfer skipped: SIP transport disconnected (race condition)',
          {
            callUuid,
            isParking,
            target: toMaskedPhoneNumber(target),
          },
        )

        // Unlike a missing session, this one is always reported to the user. Losing
        // the transport does not tear the session down - `SipClient.onDisconnect`
        // deliberately leaves the media alone because the call usually survives a
        // reconnect - so the user is still on a live call that did not transfer, and
        // needs to know to try again.
        emit(NotificationEmitterEventType.PROCESS_UPDATED, {
          disposition: NotificationDisposition.Error,
          ...notificationBase,
        })
        return
      }

      logger?.error('Blind transfer failed', error as Error, {
        callUuid,
        target: toMaskedPhoneNumber(target),
      })

      emit(NotificationEmitterEventType.PROCESS_UPDATED, {
        disposition: NotificationDisposition.Error,
        ...notificationBase,
      })
      throw error
    }
  }

  const parkCall = async (callUuid: string, parkTarget: string) => {
    await blindTransferCall(
      callUuid,
      `${PARKING_LOT_PREFIX_STRING}${parkTarget}`,
    )
  }

  const unparkCall = (parkTarget: string) => {
    if (!sipClient) return
    startCall(parkTarget, undefined, true)
  }

  const answerCall = async (callUuid?: string) => {
    const targetCallUuid = callUuid || pendingInvitationUuid
    if (!sipClient || !targetCallUuid) return

    const call = rawCallList.get(targetCallUuid)
    // Check if SIP session exists - if it does, we can answer via SIP even for deskphone calls
    // Desk phone calls without SIP sessions are answered physically and can't be answered via SIP
    if (
      call?.callSource === 'deskphone' &&
      !sipClient.hasSession(targetCallUuid)
    ) {
      logger?.info('Cannot answer desk phone call via SIP - no SIP session', {
        callUuid: targetCallUuid,
      })
      return
    }

    logger?.info('Answer call', { callUuid: targetCallUuid })

    try {
      const answered = await sipClient.answerCall(targetCallUuid)
      if (!answered) {
        // No-op answer (call already terminated or answer already in flight):
        // don't mark a call we never accepted as active or track it as answered.
        logger?.info('Answer was a no-op, skipping post-answer state updates', {
          callUuid: targetCallUuid,
        })
        return
      }
      setActiveCallUuid(targetCallUuid)
      setNeedsClientAudioInput(true)
      setNeedsAudioConnected(true)
      track(WebPhoneAnalyticsEvents.InviteAnswered)
    } catch (error: any) {
      logger?.error('Failed to answer call', error, {
        callUuid: targetCallUuid,
        error: error as Error,
      })
      announceCallFailure(t('callAnnouncements.answerFailed'))
      // If SIP answer fails, don't advance FIFO queue - let the call remain in UI
      throw error
    }
  }

  const answerCallWithHoldCurrent = async (callUuid?: string) => {
    // Find the truly active call (connected and not on hold)
    const trulyActiveCall = Array.from(rawCallList.values()).find(
      (call) => call.status === PhoneCallStatus.CONNECTED && !call.isOnHold,
    )

    if (trulyActiveCall) {
      await holdCallInternal(trulyActiveCall, true)
      // Wait for React state to propagate
      await new Promise((resolve) => setTimeout(resolve, 100))
      emit(NotificationEmitterEventType.PROCESS_UPDATED, {
        disposition: NotificationDisposition.Success,
        domain: NotificationDomain.WEBPHONE,
        operation: WebphoneOperation.CallHeld,
        context:
          trulyActiveCall.name || toMaskedPhoneNumber(trulyActiveCall.number),
      })
    }
    await answerCall(callUuid)
  }

  const answerCallWithEndCurrent = async (callUuid?: string) => {
    // Find the truly active call (connected and not on hold) instead of relying on activeCallUuid
    const trulyActiveCall = Array.from(rawCallList.values()).find(
      (call) => call.status === PhoneCallStatus.CONNECTED && !call.isOnHold,
    )

    if (trulyActiveCall) {
      await endCallInternal(trulyActiveCall)
      emit(NotificationEmitterEventType.PROCESS_UPDATED, {
        disposition: NotificationDisposition.Error,
        domain: NotificationDomain.WEBPHONE,
        operation: WebphoneOperation.CallEnded,
        context:
          trulyActiveCall.name || toMaskedPhoneNumber(trulyActiveCall.number),
      })
    }
    await answerCall(callUuid)
  }

  const answerCallWithParkCurrent = async (
    parkId: string,
    callUuid?: string,
  ) => {
    logger?.info('answerCallWithParkCurrent called', {
      parkId,
      callUuid,
    })
    // Find the truly active call (connected and not on hold) instead of relying on activeCall state
    const trulyActiveCall = Array.from(rawCallList.values()).find(
      (call) => call.status === PhoneCallStatus.CONNECTED && !call.isOnHold,
    )

    if (trulyActiveCall) {
      logger?.info('Parking call', {
        callUuid: trulyActiveCall.callUuid,
        parkId,
      })
      try {
        await parkCall(trulyActiveCall.callUuid, parkId)
      } catch (error) {
        logger?.error('Failed to park call before answering', error as Error, {
          callUuid: trulyActiveCall.callUuid,
          parkId,
        })
      }
    } else {
      logger?.debug('No truly active call found to park')
    }
    await answerCall(callUuid)
  }

  const rejectCall = async (callUuid?: string) => {
    const targetCallUuid = callUuid || pendingInvitationUuid
    logger?.info('Reject call', {
      callUuid: targetCallUuid,
      callListSize: rawCallList.size,
    })
    if (!sipClient || !targetCallUuid) {
      logger?.warn('Reject call: No SIP client or callUuid', {
        callUuid: targetCallUuid,
      })
      return
    }

    const call = rawCallList.get(targetCallUuid)
    if (
      !call ||
      call.status === PhoneCallStatus.ENDED ||
      call.status === PhoneCallStatus.ENDING_CALL
    ) {
      logger?.info('Call already ended or ending, cannot reject', {
        callUuid: targetCallUuid,
        status: call?.status,
      })
      // Advance FIFO queue if rejecting the pending invitation
      if (targetCallUuid === pendingInvitationUuid) {
        const nextRingingCall = findNextRingingCall(targetCallUuid)
        setPendingInvitationUuid(nextRingingCall?.callUuid ?? null)
      }
      return
    }

    logger?.info('Rejecting call', {
      callUuid: targetCallUuid,
      status: call.status,
      number: call.number,
      callSource: call.callSource,
    })

    // Check if SIP session exists - if it does, we can reject via SIP even for deskphone calls
    // Desk phone calls without SIP sessions are managed by WebSocket events and can't be rejected via SIP
    if (
      call.callSource === 'deskphone' &&
      !sipClient.hasSession(targetCallUuid)
    ) {
      logger?.warn('Cannot reject desk phone call via SIP - no SIP session', {
        callUuid: targetCallUuid,
      })
      return
    }

    // rejectCall now always resolves (never rejects), so we don't need try/catch
    // but we await it for correctness
    await sipClient.rejectCall(targetCallUuid)
    logger?.info('SIP rejectCall called successfully', {
      callUuid: targetCallUuid,
    })

    // Capture call info before removing it for toast notification
    const isPendingInvitation = targetCallUuid === pendingInvitationUuid
    const callNameOrNumber = call.name || toMaskedPhoneNumber(call.number)

    // Advance FIFO queue if rejecting the pending invitation
    if (isPendingInvitation) {
      const nextRingingCall = findNextRingingCall(targetCallUuid)
      setPendingInvitationUuid(nextRingingCall?.callUuid ?? null)
    }
    removeCall(targetCallUuid)

    // Emit notification for rejected call (only if it was the pending invitation)
    if (isPendingInvitation) {
      emit(NotificationEmitterEventType.PROCESS_UPDATED, {
        disposition: NotificationDisposition.Error,
        domain: NotificationDomain.WEBPHONE,
        operation: WebphoneOperation.CallDeclined,
        context: callNameOrNumber,
      })
    }
  }

  const rejectCallWithBusy = (callUuid?: string) => {
    const targetCallUuid = callUuid || pendingInvitationUuid
    if (!sipClient || !targetCallUuid) return

    const call = rawCallList.get(targetCallUuid)
    // Desk phone calls can't be rejected via SIP - they're managed by WebSocket events
    if (call?.callSource === 'deskphone') {
      logger?.warn('Cannot reject desk phone call via SIP', {
        callUuid: targetCallUuid,
      })
      return
    }

    logger?.info('Reject call with busy', { callUuid: targetCallUuid })
    void sipClient.rejectWithBusy(targetCallUuid)

    // Advance FIFO queue if rejecting the pending invitation
    if (targetCallUuid === pendingInvitationUuid) {
      const nextRingingCall = findNextRingingCall(targetCallUuid)
      setPendingInvitationUuid(nextRingingCall?.callUuid ?? null)
    }
    removeCall(targetCallUuid)
  }

  const switchCall = async (callUuid: string) => {
    const call = rawCallList.get(callUuid)
    if (!call) return

    // Desk phone calls can't be switched to via SIP - they're managed by the physical phone
    // Note: ActiveCallBlock filters these out, but this guard provides defense in depth
    if (call.callSource === 'deskphone') {
      logger?.info('Cannot switch to desk phone call via SIP', { callUuid })
      return
    }

    try {
      // Find the truly active call (connected and not on hold) instead of relying on activeCall state
      const trulyActiveCall = Array.from(rawCallList.values()).find(
        (c) =>
          c.status === PhoneCallStatus.CONNECTED &&
          !c.isOnHold &&
          c.callUuid !== callUuid,
      )

      if (trulyActiveCall) {
        await holdCallInternal(trulyActiveCall, true)
        // Wait a moment for the hold operation to complete
        await new Promise((resolve) => setTimeout(resolve, 500))
      }

      await holdCallInternal(call, false)
      setActiveCallUuid(callUuid)
      setNeedsAudioConnected(true)
      // Note: callList updates propagate to popup automatically via IPC
    } catch (error) {
      logger?.error('Switch call failed, retrying in 1 second:', error as Error)
      // Retry after a delay if the operation failed due to reinvite in progress
      setTimeout(async () => {
        try {
          const trulyActiveCall = Array.from(rawCallList.values()).find(
            (c) =>
              c.status === PhoneCallStatus.CONNECTED &&
              !c.isOnHold &&
              c.callUuid !== callUuid,
          )
          if (trulyActiveCall) {
            await holdCallInternal(trulyActiveCall, true)
            await new Promise((resolve) => setTimeout(resolve, 500))
          }
          await holdCallInternal(call, false)
          setActiveCallUuid(callUuid)
          setNeedsAudioConnected(true)
          // Note: callList updates propagate to popup automatically via IPC
        } catch (retryError) {
          logger?.error('Switch call retry failed:', retryError as Error)
        }
      }, 1000)
    }
  }

  const mergeCalls = async (callUuids: string[]) => {
    // Filter out desk phone calls - they can't be conferenced via SIP
    // Note: ActiveCallBlock filters these out, but this provides defense in depth
    const softphoneCallUuids = callUuids.filter((uuid) => {
      const call = rawCallList.get(uuid)
      return call && call.callSource !== 'deskphone'
    })

    if (softphoneCallUuids.length === 0) {
      logger?.info('No softphone calls to merge', { callUuids })
      return
    }

    addCallsToConference(softphoneCallUuids)

    const holdUpdates: Array<{ callUuid: string; isOnHold: boolean }> = []
    await Promise.all(
      Array.from(rawCallList.values()).map(async (call) => {
        if (softphoneCallUuids.includes(call.callUuid)) {
          muteCallInternal(call, false)
          if (call.isOnHold) {
            await holdCallInternal(call, false)
            holdUpdates.push({ callUuid: call.callUuid, isOnHold: false })
          }
        }
      }),
    )

    // Note: callList updates propagate to popup automatically via IPC
    setNeedsAudioConnected(true)
  }

  const splitCalls = async () => {
    endConference()

    const holdUpdates: Array<{ callUuid: string; isOnHold: boolean }> = []
    await Promise.all(
      Array.from(rawCallList.values()).map(async (call) => {
        // Only hold softphone calls - skip desk phone calls
        // Note: ActiveCallBlock filters these out, but this provides defense in depth
        if (
          call.callUuid !== activeCall?.callUuid &&
          !call.isOnHold &&
          call.callSource !== 'deskphone' &&
          call.status === PhoneCallStatus.CONNECTED
        ) {
          await holdCallInternal(call, true)
          holdUpdates.push({ callUuid: call.callUuid, isOnHold: true })
        }
      }),
    )

    if (!activeCall && rawCallList.size > 0) {
      // Prefer a softphone call as the new active call
      // Note: ActiveCallBlock filters desk phone calls, but prefer softphone for consistency
      const firstSoftphoneCall = Array.from(rawCallList.values()).find(
        (call) => call.callSource !== 'deskphone',
      )
      const firstCall =
        firstSoftphoneCall || Array.from(rawCallList.values())[0]

      if (isPhoneCall(firstCall) && firstCall.callSource !== 'deskphone') {
        setActiveCallUuid(firstCall.callUuid)
        await holdCallInternal(firstCall, false)
        holdUpdates.push({ callUuid: firstCall.callUuid, isOnHold: false })
      }
    }

    // Note: callList updates propagate to popup automatically via IPC
    setNeedsAudioConnected(true)
  }

  const endConferenceCall = () => {
    conferencedCalls?.forEach((call) => {
      endCall(call.callUuid)
    })
    endConference()
  }

  const sendDtmf = (callUuid: string, digit: string) => {
    if (!sipClient) return
    const call = rawCallList.get(callUuid)
    if (!call) return
    if (call.status !== PhoneCallStatus.CONNECTED) return

    // Desk phone calls can't receive DTMF via SIP - they're managed by the physical phone
    if (call.callSource === 'deskphone') {
      logger?.info('Cannot send DTMF to desk phone call via SIP', { callUuid })
      return
    }

    if (!sipClient.hasSession(callUuid)) return
    sipClient.sendDtmf(callUuid, digit)
  }

  const resetConnection = useCallback(() => {
    setConnectionRefreshRequested(true)
  }, [])

  const switchAudioOutput = useCallback(
    (deviceId: string) => {
      logger?.info('Switch audio output', { deviceId })
      setSelectectedOutputDeviceId(deviceId)
    },
    [logger],
  )

  const switchAudioInput = useCallback(
    (deviceId: string) => {
      logger?.info('Switch audio input', { deviceId })
      setSelectedInputDeviceId(deviceId)
    },
    [logger],
  )

  // Handle final call end callback
  useEffect(() => {
    if (needsFinalCallEnd) {
      setNeedsFinalCallEnd(false)
      if (rawCallList.size === 0) {
        onFinalCallEnd?.()
      }
    }
  }, [needsFinalCallEnd, rawCallList.size, onFinalCallEnd])

  const conferenceReturn =
    conferencedCalls && conferenceStartTime
      ? { conferencedCalls, startTime: conferenceStartTime }
      : { conferencedCalls: undefined, startTime: undefined }

  return {
    state: {
      phoneStatus,
      phoneCalls: {
        active: { activeCall },
        list: { callList: rawCallList },
        conference: {
          ...conferenceReturn,
          isMuted: conferenceIsMuted,
          isOnHold: conferenceIsOnHold,
        },
        invite: {
          pendingInvitation,
          pendingInvitationUuid,
        },
        isAtMaxCallLimit,
      },
      extensions: pbxExtensionsList,
      trackedExtensions: trackedExtensions.withPresence,
      trackedParkingLots: trackedParkingLots.withPresence,
      registrationError,
      audioDevices: {
        output: {
          devices: outputs,
          preference: selectedOutputDeviceId,
        },
        input: {
          devices: inputs,
          preference: selectedInputDeviceId,
        },
      },
      speedDial,
    },
    actions: {
      switchCall,
      startCall,
      endCall,
      dismissCall,
      endAllSoftphoneCalls,
      answerCall,
      answerCallWithHoldCurrent,
      answerCallWithEndCurrent,
      answerCallWithParkCurrent,
      rejectCall,
      rejectCallWithBusy,
      muteCall,
      holdCall,
      attendedTransferCall,
      transferCall: blindTransferCall,
      parkCall,
      unparkCall,
      mergeCalls,
      splitCalls,
      endConferenceCall,
      toggleMuteConference,
      toggleHoldConference,
      sendDtmf,
      resetConnection,
      switchAudioOutput,
      switchAudioInput,
    },
  }
}
